import type {
	FunnelStage,
	PersonaLensGroupBy,
	PersonaLensResponse,
	PersonaLensWindowDays,
	PlatformKey,
	PortalDomainDto,
	PortalSiteDto,
	RankBand,
	RecommendationAnswersPage,
	RecommendationAssessment,
	RecommendationAssessmentStatus,
	RecommendationReasonType,
	RecommendationRowCategory,
	RecommendationWindowDays,
	RecommendationsResponse,
} from "@repo/core/client";
import type {
	Property,
	PropertyPage,
	WrongDomainIssueStatusValue,
} from "@repo/database/types";
import { apiClient, readApiErrorMessage } from "@shared/lib/api-client";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { PortalSiteRequestError } from "./portal-site-errors";

// ---------------------------------------------------------------------------
// Prospecting mode (bulk AI-report imports)
// ---------------------------------------------------------------------------

/** A single row submitted to the bulk-import validate/start endpoints. */
export interface ProspectImportRow {
	value: string;
	locationCity?: string;
	locationRegion?: string;
	locationCountry?: string;
}

export type ProspectRowStatus = "ok" | "bad_domain" | "duplicate";

export interface ProspectValidationResult {
	index: number;
	value: string;
	subjectType: "BRAND" | "PRODUCT";
	domain?: string;
	seedUrl?: string;
	location: { city?: string; region?: string; country?: string };
	status: ProspectRowStatus;
	message?: string;
}

export interface ProspectListItem {
	id: string;
	companyName: string | null;
	domain: string | null;
	seedUrl: string | null;
	subjectType: string;
	locationCity: string | null;
	locationRegion: string | null;
	locationCountry: string | null;
	createdAt: string;
	report: {
		id: string;
		publicToken: string;
		status: string;
		progressPercent: number | null;
		progressMessage: string | null;
		purchaseId: string | null;
		exportUrl: string | null;
		exportPptUrl: string | null;
		completedAt: string | null;
	} | null;
	shareOfVoice: number | null;
	totalMentions: number | null;
}

export interface ProspectImportBatch {
	id: string;
	organizationId: string;
	totalCount: number;
	completedCount: number;
	failedCount: number;
	status: "running" | "complete" | "failed";
	notifyEmail: string | null;
	createdAt: string;
	updatedAt: string;
}

export const prospectsListQueryKey = (organizationId?: string) =>
	["prospects-list", organizationId] as const;

export const useProspectsListQuery = (
	organizationId: string | undefined,
	limit = 25,
) =>
	useQuery({
		queryKey: prospectsListQueryKey(organizationId),
		queryFn: async () => {
			if (!organizationId) {
				return { items: [] as ProspectListItem[], nextCursor: null };
			}
			const response = await (apiClient as any).prospects.$get({
				query: { organizationId, limit: String(limit) },
			});
			if (!response.ok) {
				throw new Error("Failed to fetch prospects");
			}
			return (await response.json()) as {
				items: ProspectListItem[];
				nextCursor: string | null;
			};
		},
		enabled: !!organizationId,
		// Poll while any prospect report is still generating.
		refetchInterval: (query) => {
			const data = query.state.data as
				| { items: ProspectListItem[] }
				| undefined;
			const anyRunning = data?.items?.some(
				(p) =>
					!p.report ||
					[
						"pending",
						"running",
						"partial",
						"processing_discovery",
					].includes(p.report.status),
			);
			return anyRunning ? 15000 : false;
		},
	});

export const prospectBatchQueryKey = (batchId?: string) =>
	["prospect-batch", batchId] as const;

export const useProspectBatchQuery = (
	batchId: string | undefined,
	organizationId: string | undefined,
) =>
	useQuery({
		queryKey: prospectBatchQueryKey(batchId),
		queryFn: async () => {
			if (!batchId || !organizationId) return null;
			const response = await (apiClient as any).prospects.batches[
				":id"
			].$get({
				param: { id: batchId },
				query: { organizationId },
			});
			if (response.status === 404) return null;
			if (!response.ok) {
				throw new Error("Failed to fetch prospect batch");
			}
			return (await response.json()) as ProspectImportBatch;
		},
		enabled: !!batchId && !!organizationId,
		refetchInterval: (query) => {
			const data = query.state.data as
				| ProspectImportBatch
				| null
				| undefined;
			return data && data.status === "running" ? 15000 : false;
		},
	});

export interface ProspectMonthlyUsage {
	used: number;
	reportCredits: number;
}

export const prospectUsageQueryKey = (organizationId?: string) =>
	["prospect-usage", organizationId] as const;

export const useProspectUsageQuery = (
	organizationId: string | undefined,
	enabled = true,
) =>
	useQuery({
		queryKey: prospectUsageQueryKey(organizationId),
		queryFn: async () => {
			if (!organizationId) return null;
			const response = await (apiClient as any).prospects.usage.$get({
				query: { organizationId },
			});
			if (!response.ok) {
				throw new Error("Failed to fetch prospect usage");
			}
			return (await response.json()) as ProspectMonthlyUsage;
		},
		enabled: !!organizationId && enabled,
		staleTime: 60 * 1000,
	});

export const useValidateProspectsMutation = () =>
	useMutation({
		mutationFn: async (input: {
			organizationId: string;
			rows: ProspectImportRow[];
		}) => {
			const response = await (apiClient as any).prospects.validate.$post({
				json: input,
			});
			if (!response.ok) {
				throw new Error("Failed to validate prospects");
			}
			const data = (await response.json()) as {
				results: ProspectValidationResult[];
			};
			return data.results;
		},
	});

export const useStartProspectImportMutation = () =>
	useMutation({
		mutationFn: async (input: {
			organizationId: string;
			rows: ProspectImportRow[];
			notifyEmail?: string;
			acknowledgeOverage?: boolean;
		}) => {
			const response = await (apiClient as any).prospects.start.$post({
				json: input,
			});
			if (!response.ok) {
				const err = (await response.json()) as {
					error?: string;
					message?: string;
					availableBalance?: number;
					requiredCredits?: number;
					overageCredits?: number;
				};
				const error = new Error(
					err.message ||
						err.error ||
						"Failed to start prospect import",
				) as Error & { code?: string; details?: typeof err };
				error.code = err.error;
				error.details = err;
				throw error;
			}
			return (await response.json()) as {
				batchId: string;
				accepted: number;
				rejected: ProspectValidationResult[];
				totalCount: number;
			};
		},
	});

/**
 * The list is per organization, so the id is part of the key: once the active
 * organization resolves, the key changes and the list is fetched for it rather
 * than served from whatever was cached while it was unknown. The bare
 * `["property-list"]` form is the "no organization yet" key — the query is
 * disabled there, so it never holds data — and it doubles as the prefix the
 * mutation call sites invalidate with to hit every organization at once.
 */
export const propertyListQueryKey = (organizationId?: string) =>
	organizationId
		? (["property-list", organizationId] as const)
		: (["property-list"] as const);

/**
 * A row of the organization property list.
 *
 * The whole property record plus three setup fields the API derives per
 * request from the property's active projects and their goals. They are not
 * the same question as `trackingReadyAt`: that column says *when* a property
 * first got there and is stamped lazily, so anything asking "is this set up"
 * reads these.
 *
 * `setupStage` splits the unfinished half in two: `none` is a property with no
 * project at all, `project` is one whose prompts are already running daily and
 * only needs a goal. `setupProjectId` names the project to add it to.
 */
export type PropertyListItem = Property & {
	trackingReady: boolean;
	setupStage: "none" | "project" | "ready";
	setupProjectId: string | null;
};

/**
 * Every property in an organization.
 *
 * Disabled until the active organization resolves. Without that gate the first
 * render of a cold-loaded deep link fires this with `organizationId: ""`, the
 * API answers 403, and the query settles into an error state that every
 * consumer reads as "this organization has no properties" — an empty property
 * switcher, an empty-state card, and (because the active property is the only
 * source of the subject type) a dashboard that renders BRAND copy for a
 * category. While it is disabled the query reports `isPending: true`, which is
 * what callers must render a skeleton on; `isLoading` is false for a disabled
 * query and would show that same empty state.
 */
export const usePropertyListQuery = (organizationId?: string) =>
	useQuery({
		queryKey: propertyListQueryKey(organizationId),
		enabled: !!organizationId,
		queryFn: async () => {
			const response = await apiClient.properties.$get({
				query: {
					organizationId: organizationId ?? "",
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch property list");
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return data.map((item: any) => ({
				...item,
				createdAt: new Date(item.createdAt),
				updatedAt: new Date(item.updatedAt),
				verifiedAt: item.verifiedAt ? new Date(item.verifiedAt) : null,
				trackingReady: item.trackingReady === true,
				setupStage: item.setupStage ?? "none",
				setupProjectId: item.setupProjectId ?? null,
			})) as PropertyListItem[];
		},
	});

/**
 * One property by id, for a property the organization's list leaves out (a
 * prospect). Doesn't retry: a 403 or 404 here means there's nothing to show.
 */
export const useUnlistedPropertyQuery = (propertyId: string | undefined) =>
	useQuery({
		queryKey: ["unlisted-property", propertyId] as const,
		enabled: !!propertyId,
		retry: false,
		queryFn: async () => {
			const response = await apiClient.properties[":id"].$get({
				param: { id: propertyId ?? "" },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch property");
			}

			const item = (await response.json()) as any;
			return {
				...item,
				createdAt: new Date(item.createdAt),
				updatedAt: new Date(item.updatedAt),
				verifiedAt: item.verifiedAt ? new Date(item.verifiedAt) : null,
				trackingReady: false,
				setupStage: "none",
				setupProjectId: null,
			} as PropertyListItem;
		},
	});

// Batch Share-of-Voice summary for the org dashboard property grid
export interface PropertySovSummary {
	shareOfVoice: number;
	previousShareOfVoice: number | null;
	totalMentions: number;
	previousTotalMentions: number | null;
	measuredAt: Date;
	/**
	 * The headline basis (consideration + decision, pooled over the last 7
	 * days) — the same number the brand dashboard hero shows. Rendered on the
	 * property cards when present; null when the property has no stage-scoped
	 * rows in the window, and the card falls back to the legacy basis.
	 */
	headline: {
		shareOfVoice: number;
		previousShareOfVoice: number | null;
		totalMentions: number;
		previousTotalMentions: number | null;
		windowDays: number;
	} | null;
}

export const propertiesSovSummaryQueryKey = (organizationId?: string) =>
	organizationId
		? (["properties-sov-summary", organizationId] as const)
		: (["properties-sov-summary"] as const);

export const usePropertiesSovSummaryQuery = (organizationId?: string) =>
	useQuery({
		queryKey: propertiesSovSummaryQueryKey(organizationId),
		queryFn: async () => {
			const response = await apiClient["property-sov"].summary.$get({
				query: {
					organizationId: organizationId ?? "",
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch Share-of-Voice summary");
			}

			const data = (await response.json()) as any[];
			const byProperty: Record<string, PropertySovSummary> = {};
			for (const item of data) {
				byProperty[item.propertyId] = {
					shareOfVoice: item.shareOfVoice ?? 0,
					previousShareOfVoice: item.previousShareOfVoice ?? null,
					totalMentions: item.totalMentions ?? 0,
					previousTotalMentions: item.previousTotalMentions ?? null,
					measuredAt: new Date(item.measuredAt),
					headline: item.headline ?? null,
				};
			}
			return byProperty;
		},
		enabled: !!organizationId,
		staleTime: 5 * 60 * 1000,
	});

/**
 * Plan 5: list LOCATION children of a BRAND parent. Scoped by org so a parent
 * outside the requesting org returns 404 (and `null` here) — the API layer
 * enforces that and we surface "no locations" rather than leaking existence.
 */
export const locationsListQueryKey = (parentId: string) =>
	["locations-list", parentId] as const;

export const useLocationsListQuery = (
	parentId: string | undefined,
	organizationId: string | undefined,
) =>
	useQuery({
		queryKey: locationsListQueryKey(parentId ?? ""),
		queryFn: async () => {
			if (!parentId || !organizationId) return [] as Property[];
			const response = await (apiClient.properties as any)[
				":id"
			].locations.$get({
				param: { id: parentId },
				query: { organizationId },
			});
			if (response.status === 404) return [] as Property[];
			if (!response.ok) {
				throw new Error("Failed to fetch locations");
			}
			const data = (await response.json()) as any[];
			return data.map((item) => ({
				...item,
				createdAt: new Date(item.createdAt),
				updatedAt: new Date(item.updatedAt),
				verifiedAt: item.verifiedAt ? new Date(item.verifiedAt) : null,
			})) as Property[];
		},
		enabled: !!parentId && !!organizationId,
		staleTime: 60 * 1000,
	});

/**
 * Plan 5 per-location SOV breakdown for a parent BRAND's dashboard. Returns
 * the latest DiscoveryMetric for each LOCATION child + equal-weighted averages
 * and absolute totals so the parent can rank locations and surface
 * "typical location" summaries at a glance.
 */
export interface LocationsSovSummary {
	locations: Array<{
		id: string;
		companyName: string | null;
		locationCity: string | null;
		locationRegion: string | null;
		locationCountry: string | null;
		latestMetric: {
			shareOfVoice: number;
			totalMentions: number;
			totalCitations: number;
			totalQueries: number;
			citationRate: number;
			measuredAt: Date;
		} | null;
		/**
		 * Top competitor in this market's own latest measurement, or null when
		 * the bucket carries no competitor data. For a CATEGORY parent — whose
		 * own share of voice is structurally absent — this is the only number
		 * a market row can report.
		 */
		leader: { name: string; shareOfVoice: number } | null;
	}>;
	summary: {
		locationCount: number;
		locationsWithData: number;
		averageSov: number;
		totalMentions: number;
		totalCitations: number;
		totalQueriesAcrossLocations: number;
	};
}

export const locationsSovSummaryQueryKey = (parentId: string) =>
	["locations-sov-summary", parentId] as const;

export const useLocationsSovSummaryQuery = (
	parentId: string | undefined,
	organizationId: string | undefined,
) =>
	useQuery({
		queryKey: locationsSovSummaryQueryKey(parentId ?? ""),
		queryFn: async () => {
			if (!parentId || !organizationId) return null;
			const response = await (apiClient.properties as any)[":id"][
				"locations-sov-summary"
			].$get({
				param: { id: parentId },
				query: { organizationId },
			});
			if (response.status === 404) return null;
			if (!response.ok) {
				throw new Error("Failed to fetch locations SOV summary");
			}
			const data = (await response.json()) as any;
			return {
				...data,
				locations: data.locations.map((l: any) => ({
					...l,
					latestMetric: l.latestMetric
						? {
								...l.latestMetric,
								measuredAt: new Date(l.latestMetric.measuredAt),
							}
						: null,
				})),
			} as LocationsSovSummary;
		},
		enabled: !!parentId && !!organizationId,
		staleTime: 60 * 1000,
	});

export const propertyQueryKey = (id: string) => ["property", id];
export const usePropertyQuery = (id: string) =>
	useQuery({
		queryKey: propertyQueryKey(id),
		queryFn: async () => {
			const response = await apiClient.properties[":id"].$get({
				param: {
					id,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch property");
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return {
				...data,
				createdAt: new Date(data.createdAt),
				updatedAt: new Date(data.updatedAt),
				verifiedAt: data.verifiedAt ? new Date(data.verifiedAt) : null,
			} as Property;
		},
	});

// Onboarding status types
export interface PropertyOnboardingStatus {
	hasAiReports: boolean;
	hasFaqGenerations: boolean;
	isVerified: boolean;
	discoveryQueryCount: number;
	competitorCount: number;
	pageCount: number;
	activeProjectCount: number;
}

export const propertyOnboardingStatusQueryKey = (propertyId: string) =>
	["property-onboarding-status", propertyId] as const;

export const usePropertyOnboardingStatusQuery = (
	propertyId: string | undefined,
) =>
	useQuery({
		queryKey: propertyOnboardingStatusQueryKey(propertyId ?? ""),
		queryFn: async () => {
			if (!propertyId) {
				return null;
			}

			const response = await apiClient.properties[":id"][
				"onboarding-status"
			].$get({
				param: { id: propertyId },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch onboarding status");
			}

			return response.json() as Promise<PropertyOnboardingStatus>;
		},
		enabled: !!propertyId,
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
	});

/**
 * Thrown by `useCreatePropertyMutation` when the API gave no usable reason —
 * callers swap it for their own localized string.
 */
export const GENERIC_CREATE_PROPERTY_ERROR = "Failed to create property";

/**
 * The server's own explanation for a failed property create, when it gave one
 * (domain conflict, missing parent, ...). Returns undefined for the generic
 * fallback so callers can fall back to their i18n string.
 */
export function serverCreatePropertyError(error: unknown): string | undefined {
	if (!(error instanceof Error)) {
		return undefined;
	}
	const message = error.message.trim();
	return message && message !== GENERIC_CREATE_PROPERTY_ERROR
		? message
		: undefined;
}

export const useCreatePropertyMutation = () => {
	return useMutation({
		mutationFn: async (input: {
			organizationId: string;
			subjectType?:
				| "BRAND"
				| "PERSON"
				| "CATEGORY"
				| "PRODUCT"
				| "LOCATION";
			domain?: string;
			name?: string;
			entityRef?: string;
			seedUrl?: string;
			/** CATEGORY only: free text about the market. Ignored elsewhere. */
			description?: string;
			parentId?: string;
			locationCity?: string;
			locationRegion?: string;
			locationCountry?: string;
			isProspect?: boolean;
		}) => {
			const response = await apiClient.properties.$post({
				json: input as any,
			});

			if (!response.ok) {
				// The API gives a real reason (domain conflict, missing
				// parent, ...) — surface it instead of the generic string so
				// the user knows what to fix.
				throw new Error(
					(await readApiErrorMessage(response)) ||
						GENERIC_CREATE_PROPERTY_ERROR,
				);
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return {
				...data,
				createdAt: new Date(data.createdAt),
				updatedAt: new Date(data.updatedAt),
				verifiedAt: data.verifiedAt ? new Date(data.verifiedAt) : null,
			} as Property;
		},
	});
};

export const useUpdatePropertyMutation = () => {
	return useMutation({
		mutationFn: async ({
			id,
			companyName,
			logo,
			verified,
			blockAiModelTrainers,
			customBlocks,
			customAllows,
			aiReportSchedule,
			aiReportScheduleDayOfWeek,
			aiReportScheduleDayOfMonth,
			aiReportRecipientEmails,
			wrongDomainAlertRecipientEmails,
			negativeMessageAlertRecipientEmails,
			defaultShowTechnicalAudit,
			defaultShowGroundingSearches,
			defaultShowBrandConsistency,
			locationCity,
			locationRegion,
			locationCountry,
			aiVisitMonthlyMaxCredits,
			isProspect,
			aliases,
		}: {
			id: string;
			companyName?: string | null;
			/**
			 * Alternate names that count as a brand mention. Sent only when
			 * provided — `undefined` leaves the stored list alone, whereas `[]`
			 * clears it.
			 */
			aliases?: string[];
			logo?: string | null;
			verified?: boolean;
			blockAiModelTrainers?: boolean;
			customBlocks?: string[];
			customAllows?: string[];
			aiReportSchedule?: "weekly" | "monthly" | "quarterly" | null;
			aiReportScheduleDayOfWeek?: number | null;
			aiReportScheduleDayOfMonth?: number | null;
			aiReportRecipientEmails?: string[];
			wrongDomainAlertRecipientEmails?: string[];
			negativeMessageAlertRecipientEmails?: string[];
			defaultShowTechnicalAudit?: boolean | null;
			defaultShowGroundingSearches?: boolean | null;
			defaultShowBrandConsistency?: boolean | null;
			locationCity?: string | null;
			locationRegion?: string | null;
			locationCountry?: string | null;
			aiVisitMonthlyMaxCredits?: number | null;
			isProspect?: boolean;
		}) => {
			const response = await apiClient.properties[":id"].$patch({
				param: { id },
				json: {
					id,
					companyName,
					logo,
					verified,
					blockAiModelTrainers,
					customBlocks,
					customAllows,
					aiReportSchedule,
					aiReportScheduleDayOfWeek,
					aiReportScheduleDayOfMonth,
					aiReportRecipientEmails,
					wrongDomainAlertRecipientEmails,
					negativeMessageAlertRecipientEmails,
					defaultShowTechnicalAudit,
					defaultShowGroundingSearches,
					defaultShowBrandConsistency,
					locationCity,
					locationRegion,
					locationCountry,
					aiVisitMonthlyMaxCredits,
					isProspect,
					aliases,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to update property");
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return {
				...data,
				createdAt: new Date(data.createdAt),
				updatedAt: new Date(data.updatedAt),
				verifiedAt: data.verifiedAt ? new Date(data.verifiedAt) : null,
			} as Property;
		},
	});
};

export interface BrandSnapshotResponse {
	id: string;
	data: Record<string, unknown>;
	userOverrides: Record<
		string,
		{
			value: unknown;
			previousValue?: unknown;
			editedAt: string;
			editedByUserId: string;
		}
	> | null;
	createdAt: string;
	/** True when this snapshot belongs to a different property (LOCATION → parent). */
	inherited?: boolean;
	/** The property id that owns this snapshot (the parent for LOCATION subjects). */
	sourcePropertyId?: string;
}

export const brandSnapshotQueryKey = (propertyId: string) =>
	["brand-snapshot", propertyId] as const;

export const useBrandSnapshotQuery = (
	propertyId: string | undefined,
	options?: { refetchInterval?: number | false },
) =>
	useQuery({
		queryKey: brandSnapshotQueryKey(propertyId ?? ""),
		queryFn: async () => {
			if (!propertyId) return null;
			const response = await (apiClient.properties as any)[
				":id"
			].snapshot.$get({
				param: { id: propertyId },
			});
			if (!response.ok) {
				throw new Error("Failed to fetch brand snapshot");
			}
			return (await response.json()) as BrandSnapshotResponse | null;
		},
		enabled: !!propertyId,
		refetchInterval: options?.refetchInterval ?? false,
	});

export const useUpdateBrandSnapshotFieldMutation = () =>
	useMutation({
		mutationFn: async (input: {
			propertyId: string;
			fieldName: string;
			value: unknown;
		}) => {
			const response = await (apiClient.properties as any)[
				":id"
			].snapshot.$patch({
				param: { id: input.propertyId },
				json: { fieldName: input.fieldName, value: input.value },
			});
			if (!response.ok) {
				throw new Error("Failed to update brand snapshot");
			}
			return (await response.json()) as { id: string };
		},
	});

export const useRebuildBrandSnapshotMutation = () =>
	useMutation({
		mutationFn: async (input: {
			propertyId: string;
			discardOverrides?: boolean;
		}) => {
			const response = await (apiClient.properties as any)[
				":id"
			].snapshot.rebuild.$post({
				param: { id: input.propertyId },
				json: { discardOverrides: input.discardOverrides ?? false },
			});
			if (!response.ok) {
				throw new Error("Failed to dispatch snapshot rebuild");
			}
			return (await response.json()) as { dispatched: boolean };
		},
	});

/**
 * Create an empty BrandSnapshot row so the user can author the snapshot by
 * hand without running a crawl. The brand-snapshot editor falls into the
 * normal edit flow once the blank row exists.
 */
export const useCreateBlankBrandSnapshotMutation = () =>
	useMutation({
		mutationFn: async (input: { propertyId: string }) => {
			const response = await (apiClient.properties as any)[
				":id"
			].snapshot["create-blank"].$post({
				param: { id: input.propertyId },
			});
			if (!response.ok) {
				throw new Error("Failed to create blank brand snapshot");
			}
			return (await response.json()) as {
				id: string;
				alreadyExisted: boolean;
			};
		},
	});

export const useDeletePropertyMutation = () => {
	return useMutation({
		mutationFn: async (id: string) => {
			const response = await apiClient.properties[":id"].$delete({
				param: { id },
			});

			if (!response.ok) {
				throw new Error("Failed to delete property");
			}
		},
	});
};

export function useVerifyPropertyMutation() {
	return useMutation({
		mutationFn: async ({
			propertyId,
			organizationId,
		}: { propertyId: string; organizationId: string }) => {
			const response = await apiClient.properties[":id"].verify.$post({
				param: { id: propertyId },
				form: {
					id: propertyId,
					organizationId: organizationId,
				},
			});
			if (!response.ok) {
				throw new Error("Failed to verify property");
			}
			return response.json();
		},
	});
}

/**
 * Hook to check property verification status with polling
 * Used during onboarding to automatically detect when events start flowing
 */
export function useCheckPropertyVerification(
	propertyId: string,
	enabled = true,
	refetchInterval = 15000, // 15 seconds
) {
	return useQuery({
		queryKey: ["property-verification", propertyId],
		queryFn: async () => {
			const response = await apiClient.properties[":id"][
				"check-verification"
			].$get({
				param: { id: propertyId },
			});

			if (!response.ok) {
				throw new Error("Failed to check property verification");
			}

			const data = (await response.json()) as any;
			return {
				verified: data.verified,
				verifiedAt: data.verifiedAt ? new Date(data.verifiedAt) : null,
			};
		},
		enabled,
		refetchInterval: (query) => {
			// Stop polling once verified
			if (query.state.data?.verified) {
				return false;
			}
			return refetchInterval;
		},
		refetchOnWindowFocus: true,
		staleTime: 0, // Always refetch on mount
	});
}

// Property Pages
export const propertyPagesQueryKey = (propertyApiKey: string) =>
	["property-pages", propertyApiKey] as const;

export function usePropertyPagesQuery(propertyApiKey: string) {
	return useQuery({
		queryKey: propertyPagesQueryKey(propertyApiKey),
		queryFn: async () => {
			if (!propertyApiKey) {
				return [];
			}
			const response = await apiClient["property-pages"].$get({
				query: { propertyApiKey },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch property pages");
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return data.map((item: any) => ({
				...item,
				createdAt: new Date(item.createdAt),
				updatedAt: new Date(item.updatedAt),
			})) as PropertyPage[];
		},
	});
}

// Lightweight page data for dropdowns (no dates, minimal fields)
export interface PageSearchResult {
	path: string;
	title: string;
	tags: string[];
}

// Property Pages Search - Optimized for dropdowns with lazy loading
export const propertyPagesSearchQueryKey = (
	propertyApiKey: string,
	search: string,
) => ["property-pages-search", propertyApiKey, search] as const;

/**
 * Search property pages with debounced query
 * Only fetches when search query is at least 2 characters
 * Returns limited results (50 max) with minimal fields for performance
 */
export function usePropertyPagesSearchQuery(
	propertyApiKey: string,
	search: string,
	options?: { enabled?: boolean },
) {
	const shouldFetch = !!propertyApiKey && search.length >= 2;

	return useQuery({
		queryKey: propertyPagesSearchQueryKey(propertyApiKey, search),
		queryFn: async () => {
			if (!propertyApiKey || search.length < 2) {
				return [];
			}
			const response = await apiClient["property-pages"].$get({
				query: {
					propertyApiKey,
					search,
					limit: "50",
				},
			});

			if (!response.ok) {
				throw new Error("Failed to search property pages");
			}

			const data = (await response.json()) as any;
			// Return minimal data - no date conversion needed
			return data as PageSearchResult[];
		},
		enabled: shouldFetch && options?.enabled !== false,
		staleTime: 30000, // Cache search results for 30 seconds
		gcTime: 60000, // Keep in cache for 1 minute
	});
}

export function useCreatePropertyPageMutation() {
	return useMutation({
		mutationFn: async ({
			propertyId,
			path,
			title,
		}: {
			propertyId: string;
			path: string;
			title: string;
		}) => {
			const response = await apiClient["property-pages"].$post({
				json: {
					propertyId,
					path,
					title,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to create property page");
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return {
				...data,
				createdAt: new Date(data.createdAt),
				updatedAt: new Date(data.updatedAt),
			} as PropertyPage;
		},
	});
}

export function useDeletePropertyPageMutation() {
	return useMutation({
		mutationFn: async (pageId: string) => {
			const response = await apiClient["property-pages"][":id"].$delete({
				param: { id: pageId },
			});

			if (!response.ok) {
				throw new Error("Failed to delete property page");
			}
		},
	});
}

// Analytics query keys and filters
export interface AnalyticsFilters {
	pagePath?: string; // Filter for specific page path
	pageIds?: string[]; // Filter by page IDs (e.g. from a page group)
	intents?: string[]; // Filter for specific page intents
	country?: string; // Filter for specific country
	region?: string; // Filter for specific region
	city?: string; // Filter for specific city
	category?: string; // Filter for specific agent category
	subcategory?: string; // Filter for specific agent subcategory
	statusCode?: number; // Filter for specific status code
	includeBlockedBreakdown?: boolean; // Flag to include blocked/allowed breakdown
}

export const propertyAnalyticsQueryKey = (
	propertyId: string,
	type:
		| "live"
		| "categories"
		| "subcategories"
		| "companies"
		| "locations"
		| "pages"
		| "blocked-stats" = "live",
	timeRange?: { start: Date; end: Date } | "live",
	filters?: { category?: string; subcategory?: string } & AnalyticsFilters,
	locationGroupBy?: "country" | "region" | "city",
) => {
	if (timeRange === "live") {
		return [
			"property-analytics",
			propertyId,
			type,
			"live",
			filters,
			locationGroupBy,
		] as const;
	}
	return [
		"property-analytics",
		propertyId,
		type,
		timeRange?.start?.toISOString(),
		timeRange?.end?.toISOString(),
		filters,
		locationGroupBy,
	] as const;
};

// Analytics types
export interface LiveAgentData {
	userAgent: string;
	category?: string;
	subcategory?: string;
	botType?: string;
	timestamp: string;
}

export interface CategoryData {
	category: string;
	count: number;
	percentage: number;
	blocked_count?: number;
	blocked_percentage?: number;
	allowed_count?: number;
	allowed_percentage?: number;
}

export interface SubcategoryData {
	subcategory: string;
	count: number;
	percentage: number;
	blocked_count?: number;
	blocked_percentage?: number;
	allowed_count?: number;
	allowed_percentage?: number;
}

export interface CompanyData {
	company: string;
	count: number;
	percentage: number;
	blocked_count?: number;
	blocked_percentage?: number;
	allowed_count?: number;
	allowed_percentage?: number;
	botTypes: string[];
}

export interface LocationData {
	country_code: string;
	region?: string;
	city?: string;
	count: number;
	percentage: number;
	blocked_count?: number;
	blocked_percentage?: number;
	allowed_count?: number;
	allowed_percentage?: number;
}

export interface PageData {
	path: string;
	title?: string;
	count: number;
	percentage: number;
	blocked_count?: number;
	blocked_percentage?: number;
	allowed_count?: number;
	allowed_percentage?: number;
}

export interface BlockedRequestsStats {
	total_requests: number;
	blocked_requests: number;
	allowed_requests: number;
	blocked_percentage: number;
	allowed_percentage: number;
}

export interface AnalyticsResponse<T> {
	success: boolean;
	data?: T[];
	error?: string;
}

export interface BlockedStatsResponse {
	success: boolean;
	data?: BlockedRequestsStats;
	error?: string;
}

/**
 * Helper function to resolve property API key.
 * If apiKey is provided directly, uses it. Otherwise fetches from API.
 * This optimization reduces redundant property fetches when apiKey is already known.
 */
async function resolvePropertyApiKey(
	propertyId: string,
	providedApiKey?: string,
): Promise<string> {
	if (providedApiKey) {
		return providedApiKey;
	}

	const propertyResponse = await apiClient.properties[":id"].$get({
		param: { id: propertyId },
	});

	if (!propertyResponse.ok) {
		throw new Error("Failed to fetch property");
	}

	const property = (await propertyResponse.json()) as any;
	return property.apiKey;
}

// Analytics hooks
export function usePropertyAnalytics(
	propertyId: string,
	type:
		| "live"
		| "categories"
		| "subcategories"
		| "companies"
		| "locations"
		| "pages"
		| "blocked-stats" = "live",
	timeRange?: { start: Date; end: Date } | "live",
	filters?: { category?: string; subcategory?: string } & AnalyticsFilters,
	locationGroupBy?: "country" | "region" | "city",
	providedApiKey?: string, // Optional: skip property fetch if apiKey is known
) {
	return useQuery({
		queryKey: propertyAnalyticsQueryKey(
			propertyId,
			type,
			timeRange,
			filters,
			locationGroupBy,
		),
		queryFn: async () => {
			const apiKey = await resolvePropertyApiKey(
				propertyId,
				providedApiKey,
			);

			// Then fetch the analytics data — typed structurally because each
			// branch returns a differently-parameterized hono ClientResponse.
			let response: { ok: boolean; json(): Promise<unknown> };

			// Prepare timeRange for all requests
			const preparedTimeRange =
				timeRange === "live"
					? {
							start: new Date(
								Date.now() - 30 * 60 * 1000,
							).toISOString(), // Last 30 minutes for live data
							end: new Date().toISOString(),
						}
					: timeRange
						? {
								start: timeRange.start.toISOString(),
								end: timeRange.end.toISOString(),
							}
						: {
								start: new Date(
									Date.now() - 30 * 24 * 60 * 60 * 1000,
								).toISOString(), // Last 30 days
								end: new Date().toISOString(),
							};

			// Create a clean filters object with all relevant filters
			const requestFilters = {
				...(filters?.pagePath && { pagePath: filters.pagePath }),
				...(filters?.pageIds?.length && { pageIds: filters.pageIds }),
				...(filters?.country && { country: filters.country }),
				...(filters?.region && { region: filters.region }),
				...(filters?.city && { city: filters.city }),
				...(filters?.category && { category: filters.category }),
				...(filters?.subcategory && {
					subcategory: filters.subcategory,
				}),
				...(filters?.statusCode && { statusCode: filters.statusCode }),
				...(filters?.includeBlockedBreakdown !== undefined && {
					includeBlockedBreakdown: filters.includeBlockedBreakdown,
				}),
			};

			switch (type) {
				case "live":
					response = await apiClient.analytics.agent.live[
						":propertyId"
					].$get({
						param: { propertyId: apiKey },
						// Live endpoint doesn't support filters yet, would need to be updated on backend
					});
					break;
				case "blocked-stats":
					response = await apiClient.analytics.agent[
						"blocked-stats"
					].$post({
						json: {
							propertyId: apiKey,
							timeRange: preparedTimeRange,
							filters: requestFilters,
						},
					});
					break;
				case "categories":
					response = await apiClient.analytics.agent.category.$post({
						json: {
							propertyId: apiKey,
							timeRange: preparedTimeRange,
							filters: requestFilters,
						},
					});
					break;
				case "subcategories":
					// Subcategories require a category filter
					if (!filters?.category) {
						throw new Error(
							"Category filter is required for subcategories analytics",
						);
					}

					response =
						await apiClient.analytics.agent.subcategory.$post({
							json: {
								propertyId: apiKey,
								category: filters.category,
								timeRange: preparedTimeRange,
								filters: requestFilters,
							},
						});
					break;
				case "companies":
					// Companies (bot-types) require a subcategory filter
					if (!filters?.subcategory) {
						throw new Error(
							"Subcategory filter is required for companies analytics",
						);
					}

					response = await apiClient.analytics.agent[
						"bot-type"
					].$post({
						json: {
							propertyId: apiKey,
							subcategory: filters.subcategory,
							timeRange: preparedTimeRange,
							filters: requestFilters,
						},
					});
					break;
				case "locations":
					response = await apiClient.analytics.agent.location.$post({
						json: {
							propertyId: apiKey,
							timeRange: preparedTimeRange,
							filters: requestFilters,
							groupBy: locationGroupBy || "country",
						},
					});
					break;
				case "pages":
					response = await apiClient.analytics.agent.pages.$post({
						json: {
							propertyId: apiKey,
							timeRange: preparedTimeRange,
							filters: requestFilters,
						},
					});
					break;
				default:
					throw new Error(`Invalid analytics type: ${type}`);
			}

			if (!response.ok) {
				throw new Error(`Failed to fetch ${type} analytics`);
			}

			const data = (await response.json()) as any;

			if (type === "blocked-stats") {
				return data as BlockedStatsResponse;
			}

			return data as AnalyticsResponse<
				| LiveAgentData
				| CategoryData
				| SubcategoryData
				| CompanyData
				| LocationData
				| PageData
			>;
		},
		refetchInterval: timeRange === "live" ? 30000 : false, // Refetch every 30 seconds for live data
	});
}

// Convenience hook for blocked requests stats
export function useBlockedRequestsStats(
	propertyId: string,
	timeRange?: { start: Date; end: Date } | "live",
	filters?: AnalyticsFilters,
	apiKey?: string, // Optional: skip property fetch if apiKey is known
) {
	return usePropertyAnalytics(
		propertyId,
		"blocked-stats",
		timeRange,
		filters,
		undefined,
		apiKey,
	);
}

export async function getProperty(id: string): Promise<Property | null> {
	try {
		const response = await apiClient.properties[":id"].$get({
			param: { id },
		});
		const data = (await response.json()) as any;

		// Check if the response contains an error
		if ("error" in data) {
			console.error("Error in property response:", data.error);
			return null;
		}

		// Convert string dates to Date objects
		return {
			...data,
			createdAt: new Date(data.createdAt),
			updatedAt: new Date(data.updatedAt),
			verifiedAt: data.verifiedAt ? new Date(data.verifiedAt) : null,
		} as Property;
	} catch (error) {
		console.error("Error fetching property:", error);
		return null;
	}
}

// AI Visibility Rankings types
export interface AIVisibilityRanking {
	groundingSearchId: string;
	searchQuery: string;
	usageCount: number;
	discoveryQueries: Array<{
		id: string;
		query: string;
	}>;
	/** Platforms that produced this search (e.g. openai, gemini, claude) for Source column */
	platforms?: string[];
	/** True when Google SERP didn't run (Claude-only search); show Google as N/A */
	googleUnavailable?: boolean;
	/** True when Brave SERP didn't run (no Claude platform); show Brave as N/A */
	braveUnavailable?: boolean;
	braveError?: boolean;
	brandRankGoogle: number | null;
	brandUrlGoogle: string | null;
	brandRankBrave: number | null;
	brandUrlBrave: string | null;
	isGapGoogle: boolean;
	isGapBrave: boolean;
	isGapBoth: boolean;
	competitorRankings: Array<{
		competitorId: string;
		competitorName: string;
		competitorDomain: string;
		rankGoogle: number | null;
		urlGoogle: string | null;
		rankBrave: number | null;
		urlBrave: string | null;
	}>;
	impactScore: number;
	/**
	 * The domain a `site:`-scoped fan-out pointed at, else null.
	 *
	 * Non-null rows are EVIDENCE, never opportunities: the model chose the site
	 * itself, so there is no ranking to win. They arrive neutered (no gap flags,
	 * zero impact, both engines unavailable) and every consumer must split on
	 * this field before treating a row as actionable — the picker greys them
	 * out, the deep-link resolver refuses them, and the KPI counts exclude them.
	 */
	siteScopeDomain?: string | null;
	/** Whose site that was, from this property's point of view. */
	siteScopeClass?: "own" | "competitor" | "third_party" | null;
	/** Competitor display name when `siteScopeClass` is "competitor". */
	siteScopeCompetitorName?: string | null;
}

export interface AIVisibilityRankingsData {
	reportId: string | null;
	reportCompletedAt: Date | null;
	rankings: AIVisibilityRanking[];
	totalGaps: number;
	totalSearches: number;
}

// AI Visibility Rankings query key and hook
export const aiVisibilityRankingsQueryKey = (propertyId: string) =>
	["ai-visibility-rankings", propertyId] as const;

export const useAIVisibilityRankingsQuery = (propertyId: string) =>
	useQuery({
		queryKey: aiVisibilityRankingsQueryKey(propertyId),
		queryFn: async () => {
			const response = await apiClient.properties[":id"][
				"ai-visibility-rankings"
			].$get({
				param: { id: propertyId },
			});

			if (!response.ok) {
				if (response.status === 404) {
					// No rankings data available
					return {
						reportId: null,
						reportCompletedAt: null,
						rankings: [],
						totalGaps: 0,
						totalSearches: 0,
					};
				}
				throw new Error("Failed to fetch AI visibility rankings");
			}

			const data = (await response.json()) as any;
			return {
				...data,
				reportCompletedAt: data.reportCompletedAt
					? new Date(data.reportCompletedAt)
					: null,
			} as AIVisibilityRankingsData;
		},
		enabled: !!propertyId,
		staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
	});

// Historical Metrics Types
export interface BrandConsistencyMetric {
	id: string;
	/** Null on scheduled checks — those aren't tied to a report. */
	reportId: string | null;
	propertyId: string;
	/** "weekly" (the scheduled cadence job, now monthly) or "report" (AI Visibility Report). */
	source: string;
	measuredAt: Date;
	overallScore: number;
	/**
	 * Per-platform scores, stored under the "chatgpt" prefix rather than the
	 * canonical "openai" LLM key. Weekly rows populate these; report rows
	 * leave them null (the report keeps its detail on the report record).
	 */
	chatgptScore: number | null;
	chatgptBrowsingScore: number | null;
	chatgptStandardScore: number | null;
	geminiScore: number | null;
	geminiBrowsingScore: number | null;
	geminiStandardScore: number | null;
	perplexityScore: number | null;
	perplexityBrowsingScore: number | null;
	perplexityStandardScore: number | null;
	claudeScore: number | null;
	claudeBrowsingScore: number | null;
	claudeStandardScore: number | null;
	report: {
		id: string;
		publicToken: string;
		startedAt: Date;
		completedAt: Date;
	} | null;
}

export interface DiscoveryMetric {
	id: string;
	reportId: string;
	propertyId: string;
	measuredAt: Date;
	totalQueries: number;
	totalMentions: number;
	shareOfVoice: number;
	totalCitations: number;
	citationRate: number;
	competitorMetrics: any;
	/**
	 * Raw execution platform ids the row measured (`isPlatformMeasured` in
	 * @repo/core/client). Empty on rows written before the column existed.
	 */
	measuredPlatforms?: string[];
	report: {
		id: string;
		publicToken: string;
		startedAt: Date;
		completedAt: Date;
	};
}

export interface GroundingSearchMetric {
	id: string;
	reportId: string;
	propertyId: string;
	groundingSearchId: string;
	measuredAt: Date;
	brandRankPosition: number | null;
	bestCompetitorRankPosition: number | null;
	isGapOpportunity: boolean;
	queryFrequency: number;
	groundingSearch: {
		id: string;
		query: string;
	};
}

// Historical Metrics Query Keys and Hooks
/** Response of GET /properties/:id/goal-influence (PropertyGoalInfluence in core). */
export interface PropertyGoalInfluenceWindow {
	matchedCitations: number;
	attributedCitations: number;
	totalCitations: number;
	influenceShare: number | null;
}

export interface PropertyGoalInfluenceData {
	propertyId: string;
	activeProjectsWithGoals: number;
	windowDays: number;
	current: PropertyGoalInfluenceWindow;
	previous: PropertyGoalInfluenceWindow | null;
}

export const propertyGoalInfluenceQueryKey = (
	propertyId: string,
	days?: number,
) => ["property-goal-influence", propertyId, days ?? 7] as const;

/**
 * Windowed goal influence across the property's ACTIVE projects — the brand
 * dashboard's Goal impact card. Distinct-citation semantics are computed
 * server-side; never sum per-project influence client-side (double counts).
 */
export const usePropertyGoalInfluenceQuery = (
	propertyId: string | undefined,
	days?: number,
	options?: { enabled?: boolean },
) =>
	useQuery({
		queryKey: propertyGoalInfluenceQueryKey(propertyId ?? "", days),
		queryFn: async () => {
			const response = await (apiClient.properties as any)[":id"][
				"goal-influence"
			].$get({
				param: { id: propertyId },
				query: days ? { days: String(days) } : {},
			});
			if (!response.ok) {
				throw new Error("Failed to fetch property goal influence");
			}
			return (await response.json()) as PropertyGoalInfluenceData;
		},
		enabled: !!propertyId && (options?.enabled ?? true),
		staleTime: 10 * 60 * 1000,
	});

// ---------------------------------------------------------------------------
// Site consultations — GET /ai-rankings/:propertyId/site-consultations
// ---------------------------------------------------------------------------

/** Consultation counts split by whose site the model went to. */
export interface SiteConsultationCounts {
	own: number;
	competitor: number;
	thirdParty: number;
}

export interface SiteConsultationPlatformRow extends SiteConsultationCounts {
	platform: string;
}

export interface SiteConsultationStageRow extends SiteConsultationCounts {
	stage: "awareness" | "consideration" | "decision";
}

export interface SiteConsultationDomainRow {
	domain: string;
	class: "own" | "competitor" | "third_party";
	/** Set only when `class === "competitor"`. */
	competitorId: string | null;
	/** Set only when `class === "competitor"`. */
	competitorName: string | null;
	/**
	 * A `third_party` domain that looks mistaken for this brand's own site.
	 * Only these rows offer the claim/dismiss actions — every other third
	 * party is legitimate context nobody needs to answer for.
	 */
	brandAffine: boolean;
	count: number;
	/**
	 * How many of `count` came from brand-identity/comparison prompts — the
	 * consultations the headline numbers exclude. Lets the table reconcile
	 * its counts against the KPIs per row.
	 */
	brandPromptCount: number;
	platforms: string[];
	lastSeen: Date;
	/**
	 * The underlying topic — the query with the `site:` operator stripped. Empty
	 * for a bare `site:acme.com`, which is itself informative.
	 */
	residualQuery: string;
	/**
	 * Status of the wrong-domain issue (investigation) for this domain, or
	 * null when there is none. Flagged rows link to the investigation page.
	 */
	issueStatus: WrongDomainIssueStatusValue | null;
}

/** Response of GET /ai-rankings/:propertyId/site-consultations. */
export interface SiteConsultationMetrics {
	propertyId: string;
	days: number;
	windowStart: Date;
	previousWindowStart: Date;
	current: SiteConsultationCounts;
	previous: SiteConsultationCounts;
	/** `current - previous`, per class. */
	delta: SiteConsultationCounts;
	/** Share of searching executions that consulted our own site, 0–1. */
	consultRate: number;
	consultedExecutions: number;
	totalExecutions: number;
	byPlatform: SiteConsultationPlatformRow[];
	byFunnelStage: SiteConsultationStageRow[];
	domains: SiteConsultationDomainRow[];
}

export const siteConsultationsQueryKey = (propertyId: string, days?: number) =>
	["site-consultations", propertyId, days ?? 7] as const;

/**
 * `site:`-scoped fan-outs for a property — who the model consults directly
 * instead of searching the open web. The endpoint returns the current window
 * AND the previous one of the same length, so the delta never needs a second
 * fetch.
 */
export const useSiteConsultationsQuery = (
	propertyId: string | undefined,
	days?: number,
	options?: { enabled?: boolean },
) =>
	useQuery({
		queryKey: siteConsultationsQueryKey(propertyId ?? "", days),
		queryFn: async () => {
			const response = await (apiClient as any)["ai-rankings"][
				":propertyId"
			]["site-consultations"].$get({
				param: { propertyId },
				query: days ? { days: String(days) } : {},
			});
			if (!response.ok) {
				throw new Error("Failed to fetch site consultations");
			}
			const data = (await response.json()) as any;
			return {
				...data,
				windowStart: new Date(data.windowStart),
				previousWindowStart: new Date(data.previousWindowStart),
				domains: (data.domains ?? []).map((row: any) => ({
					...row,
					lastSeen: new Date(row.lastSeen),
				})),
			} as SiteConsultationMetrics;
		},
		enabled: !!propertyId && (options?.enabled ?? true),
		staleTime: 10 * 60 * 1000,
	});

/**
 * "This domain is ours" — append a legitimate non-canonical domain.
 *
 * Reclassifies every historical consultation of it as `own`, so the
 * consultations query is invalidated rather than optimistically patched: the
 * KPI tiles above the table move too, and guessing their new values client-side
 * would show numbers that disagree with the server for a beat.
 */
export const useAddAdditionalDomainMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			propertyId,
			domain,
		}: {
			propertyId: string;
			domain: string;
		}) => {
			const response = await (apiClient as any).properties[":id"][
				"additional-domains"
			].$post({
				param: { id: propertyId },
				json: { domain },
			});
			if (!response.ok) {
				throw new Error("Failed to add domain");
			}
			return (await response.json()) as {
				additionalDomains: string[];
				added: boolean;
			};
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["site-consultations", variables.propertyId],
			});
			// The route also closes the domain's wrong-domain issue.
			queryClient.invalidateQueries({
				queryKey: ["wrong-domain-issues", variables.propertyId],
			});
			queryClient.invalidateQueries({
				queryKey: ["wrong-domain-issue", variables.propertyId],
			});
		},
	});
};

/** "Dismiss" — stop alerting on a domain without claiming it. */
export const useSuppressConsultationDomainMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			propertyId,
			domain,
		}: {
			propertyId: string;
			domain: string;
		}) => {
			const response = await (apiClient as any).properties[":id"][
				"suppressed-consultation-domains"
			].$post({
				param: { id: propertyId },
				json: { domain },
			});
			if (!response.ok) {
				throw new Error("Failed to dismiss domain");
			}
			return (await response.json()) as {
				suppressedConsultationDomains: string[];
				added: boolean;
			};
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["site-consultations", variables.propertyId],
			});
			// The route also closes the domain's wrong-domain issue.
			queryClient.invalidateQueries({
				queryKey: ["wrong-domain-issues", variables.propertyId],
			});
			queryClient.invalidateQueries({
				queryKey: ["wrong-domain-issue", variables.propertyId],
			});
		},
	});
};

export interface HistoricalMetricsFilterOptions {
	startDate?: Date;
	endDate?: Date;
	/**
	 * Scope prompt-based series to one project's prompts. Supported by the
	 * discovery, brand-prompts, and comparisons endpoints (server-side
	 * recompute); prompt-independent series (consistency, attributes) ignore it.
	 */
	projectId?: string;
}

export const brandConsistencyHistoryQueryKey = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions,
) =>
	[
		"brand-consistency-history",
		propertyId,
		filters?.startDate?.toISOString(),
		filters?.endDate?.toISOString(),
	] as const;

export const useBrandConsistencyHistoryQuery = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions,
) =>
	useQuery({
		queryKey: brandConsistencyHistoryQueryKey(propertyId, filters),
		queryFn: async () => {
			const response = await apiClient.properties[":id"][
				"historical-metrics"
			]["brand-consistency"].$get({
				param: { id: propertyId },
				query: {
					startDate: filters?.startDate?.toISOString(),
					endDate: filters?.endDate?.toISOString(),
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch brand consistency history");
			}

			const data = (await response.json()) as any;
			return (data as any[]).map((metric) => ({
				...metric,
				measuredAt: new Date(metric.measuredAt),
				report: metric.report
					? {
							...metric.report,
							startedAt: new Date(metric.report.startedAt),
							completedAt: new Date(metric.report.completedAt),
						}
					: null,
			})) as BrandConsistencyMetric[];
		},
		enabled: !!propertyId,
		staleTime: 10 * 60 * 1000, // 10 minutes
	});

/**
 * Plan 5 location scope for the discovery metrics endpoint:
 *   - `"brand"` / undefined: brand-wide rollup only (default)
 *   - `"all"`: mixed series across parent + every location
 *   - a string: only that LOCATION child's rows
 */
export type DiscoveryMetricsLocationScope = "brand" | "all" | string;

export const discoveryMetricsHistoryQueryKey = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions,
	scope?: DiscoveryMetricsLocationScope,
) =>
	[
		"discovery-metrics-history",
		propertyId,
		filters?.startDate?.toISOString(),
		filters?.endDate?.toISOString(),
		filters?.projectId ?? null,
		scope ?? "brand",
	] as const;

export const useDiscoveryMetricsHistoryQuery = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions,
	scope?: DiscoveryMetricsLocationScope,
	options?: { enabled?: boolean },
) =>
	useQuery({
		queryKey: discoveryMetricsHistoryQueryKey(propertyId, filters, scope),
		queryFn: async () => {
			const query: Record<string, string | undefined> = {
				startDate: filters?.startDate?.toISOString(),
				endDate: filters?.endDate?.toISOString(),
				...(filters?.projectId ? { projectId: filters.projectId } : {}),
			};
			if (scope === "all") {
				query.locationScope = "all";
			} else if (scope && scope !== "brand") {
				query.locationPropertyId = scope;
			}
			const response = await (apiClient.properties as any)[":id"][
				"historical-metrics"
			].discovery.$get({
				param: { id: propertyId },
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to fetch discovery metrics history");
			}

			const data = (await response.json()) as any;
			return (data as any[]).map((metric) => ({
				...metric,
				measuredAt: new Date(metric.measuredAt),
				report: metric.report
					? {
							...metric.report,
							startedAt: new Date(metric.report.startedAt),
							completedAt: new Date(metric.report.completedAt),
						}
					: null,
			})) as DiscoveryMetric[];
		},
		enabled: !!propertyId && options?.enabled !== false,
		staleTime: 10 * 60 * 1000,
	});

export interface AttributeMentionMetric {
	id: string;
	reportId: string | null;
	propertyId: string;
	keyMessageId: string;
	source: string;
	sentiment: "POSITIVE" | "NEGATIVE" | "NEUTRAL";
	mentionCount: number;
	totalExecutions: number;
	mentionRate: number;
	platformBreakdown: Record<string, { mentions: number; executions: number }>;
	measuredAt: Date;
	keyMessage?: { id: string; message: string; sentiment: string } | null;
}

export const attributeMetricsHistoryQueryKey = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions,
) =>
	[
		"attribute-metrics-history",
		propertyId,
		filters?.startDate?.toISOString(),
		filters?.endDate?.toISOString(),
	] as const;

export const useAttributeMetricsHistoryQuery = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions,
) =>
	useQuery({
		queryKey: attributeMetricsHistoryQueryKey(propertyId, filters),
		queryFn: async () => {
			const response = await apiClient.properties[":id"][
				"historical-metrics"
			].attributes.$get({
				param: { id: propertyId },
				query: {
					startDate: filters?.startDate?.toISOString(),
					endDate: filters?.endDate?.toISOString(),
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch attribute metrics history");
			}

			const data = (await response.json()) as any;
			return (data as any[]).map((metric) => ({
				...metric,
				measuredAt: new Date(metric.measuredAt),
			})) as AttributeMentionMetric[];
		},
		enabled: !!propertyId,
		staleTime: 10 * 60 * 1000,
	});

export type KeyMessageSentimentFilter = "POSITIVE" | "NEGATIVE" | "NEUTRAL";

export interface MessageTrackingSeriesPoint {
	measuredAt: Date;
	mentionRate: number;
	mentionCount: number;
	totalExecutions: number;
}

/** Mirrors `MessageTrackingEntry` from @repo/core (server-only module). */
export interface MessageTrackingEntry {
	keyMessageId: string;
	message: string;
	sentiment: string;
	tags: string[];
	/** Ascending by measuredAt. */
	series: MessageTrackingSeriesPoint[];
	firstRate: number | null;
	latestRate: number | null;
	/** latestRate − firstRate, in percentage points. */
	changePoints: number | null;
}

export interface MessageTrackingFilterOptions
	extends HistoricalMetricsFilterOptions {
	/** Messages carrying at least one of these tags. */
	tags?: string[];
	sentiment?: KeyMessageSentimentFilter;
}

export const messageTrackingQueryKey = (
	propertyId: string,
	filters?: MessageTrackingFilterOptions,
) =>
	[
		"message-tracking",
		propertyId,
		filters?.startDate?.toISOString(),
		filters?.endDate?.toISOString(),
		filters?.tags?.join(",") ?? null,
		filters?.sentiment ?? null,
	] as const;

export const useMessageTrackingQuery = (
	propertyId: string,
	filters?: MessageTrackingFilterOptions,
) =>
	useQuery({
		queryKey: messageTrackingQueryKey(propertyId, filters),
		queryFn: async () => {
			const response = await apiClient.properties[":id"][
				"message-tracking"
			].$get({
				param: { id: propertyId },
				query: {
					startDate: filters?.startDate?.toISOString(),
					endDate: filters?.endDate?.toISOString(),
					// Comma-separated, matching the route's list-param shape.
					...(filters?.tags?.length
						? { tags: filters.tags.join(",") }
						: {}),
					...(filters?.sentiment
						? { sentiment: filters.sentiment }
						: {}),
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch message tracking");
			}

			const data = (await response.json()) as any;
			return (data as any[]).map((entry) => ({
				...entry,
				series: (entry.series ?? []).map((point: any) => ({
					...point,
					measuredAt: new Date(point.measuredAt),
				})),
			})) as MessageTrackingEntry[];
		},
		enabled: !!propertyId,
		staleTime: 10 * 60 * 1000,
	});

/** Mirrors `KeyMessageMatchedAnswer` from @repo/core (server-only module). */
export interface KeyMessageMatchedAnswer {
	executionId: string;
	platform: string;
	score: number;
	sentence: string;
	measuredAt: Date;
	source: string;
	locationPropertyId: string | null;
	promptText: string | null;
	queryId: string | null;
}

/** Mirrors `KeyMessageMatchSource` from @repo/core. */
export interface KeyMessageMatchSource {
	domain: string;
	citedExecutions: number;
	sampleUrl: string;
	sampleTitle: string | null;
	isOurDomain: boolean;
	lastUpdated: Date | null;
}

export interface KeyMessageMatchesResult {
	keyMessage: {
		id: string;
		message: string;
		sentiment: string;
		tags: string[];
	};
	answers: KeyMessageMatchedAnswer[];
	total: number;
	sources: KeyMessageMatchSource[];
}

export interface KeyMessageMatchesFilterOptions
	extends HistoricalMetricsFilterOptions {
	platform?: string;
	limit?: number;
	offset?: number;
}

export const keyMessageMatchesQueryKey = (
	propertyId: string,
	keyMessageId: string,
	filters?: KeyMessageMatchesFilterOptions,
) =>
	[
		"key-message-matches",
		propertyId,
		keyMessageId,
		filters?.startDate?.toISOString(),
		filters?.endDate?.toISOString(),
		filters?.platform ?? null,
		filters?.limit ?? null,
		filters?.offset ?? null,
	] as const;

/**
 * The matched answers behind one key message, plus the domains cited in those
 * same answers. The key message is a query parameter, not a path segment —
 * see the route-conflict note on the endpoint.
 */
export const useKeyMessageMatchesQuery = (
	propertyId: string,
	keyMessageId: string,
	filters?: KeyMessageMatchesFilterOptions,
) =>
	useQuery({
		queryKey: keyMessageMatchesQueryKey(propertyId, keyMessageId, filters),
		queryFn: async () => {
			const response = await apiClient.properties[":id"][
				"message-tracking"
			].matches.$get({
				param: { id: propertyId },
				query: {
					keyMessageId,
					startDate: filters?.startDate?.toISOString(),
					endDate: filters?.endDate?.toISOString(),
					...(filters?.platform
						? { platform: filters.platform }
						: {}),
					...(filters?.limit != null
						? { limit: String(filters.limit) }
						: {}),
					...(filters?.offset != null
						? { offset: String(filters.offset) }
						: {}),
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch key message matches");
			}

			const data = (await response.json()) as any;
			return {
				keyMessage: data.keyMessage,
				total: data.total ?? 0,
				answers: (data.answers ?? []).map((answer: any) => ({
					...answer,
					measuredAt: new Date(answer.measuredAt),
				})),
				sources: (data.sources ?? []).map((source: any) => ({
					...source,
					lastUpdated: source.lastUpdated
						? new Date(source.lastUpdated)
						: null,
				})),
			} as KeyMessageMatchesResult;
		},
		enabled: !!propertyId && !!keyMessageId,
		staleTime: 10 * 60 * 1000,
	});

export interface BrandPromptMessageBreakdownEntry {
	keyMessageId: string;
	message: string;
	sentiment: "POSITIVE" | "NEGATIVE" | "NEUTRAL";
	mentionCount: number;
	totalExecutions: number;
	mentionRate: number;
	topSnippet: string | null;
}

export interface BrandPromptMetric {
	id: string;
	reportId: string | null;
	propertyId: string;
	propertyQueryId: string;
	source: string;
	positiveMessageCoverage: number | null;
	negativeMessageIncidence: number | null;
	driftScore: number | null;
	themes: string[] | null;
	/** Today's themes with no close match in the trailing 28-day pool. */
	novelThemes: string[] | null;
	/** Per expected-message match detail (which messages appeared, at what rate). */
	messageBreakdown: BrandPromptMessageBreakdownEntry[] | null;
	totalExecutions: number;
	/** Keyed by normalized platform (chatgpt/gemini/perplexity/claude/googleAiOverview). */
	platformBreakdown: Record<
		string,
		{
			executions: number;
			positiveCoverage: number | null;
			negativeIncidence: number | null;
		}
	>;
	measuredAt: Date;
	propertyQuery?: { id: string; query: string; queryType: string } | null;
}

export const brandPromptMetricsHistoryQueryKey = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions & { queryId?: string },
) =>
	[
		"brand-prompt-metrics-history",
		propertyId,
		filters?.startDate?.toISOString(),
		filters?.endDate?.toISOString(),
		filters?.queryId ?? null,
		filters?.projectId ?? null,
	] as const;

export const useBrandPromptMetricsHistoryQuery = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions & { queryId?: string },
) =>
	useQuery({
		queryKey: brandPromptMetricsHistoryQueryKey(propertyId, filters),
		queryFn: async () => {
			const response = await (apiClient.properties as any)[":id"][
				"historical-metrics"
			]["brand-prompts"].$get({
				param: { id: propertyId },
				query: {
					startDate: filters?.startDate?.toISOString(),
					endDate: filters?.endDate?.toISOString(),
					queryId: filters?.queryId,
					projectId: filters?.projectId,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch brand prompt metrics history");
			}

			const data = (await response.json()) as any;
			return (data as any[]).map((metric) => ({
				...metric,
				measuredAt: new Date(metric.measuredAt),
			})) as BrandPromptMetric[];
		},
		enabled: !!propertyId,
		staleTime: 10 * 60 * 1000,
	});

export interface ComparisonMetric {
	id: string;
	reportId: string | null;
	propertyId: string;
	source: string;
	comparedBrandName: string;
	isOurBrand: boolean;
	favoredCount: number;
	totalComparisons: number;
	sampleFramingSummary: string | null;
	verdictShapeCounts: Record<string, number>;
	/** What answers favor this brand FOR — the context behind favoredCount,
	 *  which is not zero-sum (segmented answers favor several brands). */
	topFavoredFor: Array<{ text: string; count: number }> | null;
	topStrengths: Array<{ text: string; count: number }>;
	topWeaknesses: Array<{ text: string; count: number }>;
	platformBreakdown: Record<string, { favored: number; total: number }>;
	measuredAt: Date;
}

export const comparisonMetricsHistoryQueryKey = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions,
) =>
	[
		"comparison-metrics-history",
		propertyId,
		filters?.startDate?.toISOString(),
		filters?.endDate?.toISOString(),
		filters?.projectId ?? null,
	] as const;

export const useComparisonMetricsHistoryQuery = (
	propertyId: string,
	filters?: HistoricalMetricsFilterOptions,
) =>
	useQuery({
		queryKey: comparisonMetricsHistoryQueryKey(propertyId, filters),
		queryFn: async () => {
			const response = await (apiClient.properties as any)[":id"][
				"historical-metrics"
			].comparisons.$get({
				param: { id: propertyId },
				query: {
					startDate: filters?.startDate?.toISOString(),
					endDate: filters?.endDate?.toISOString(),
					projectId: filters?.projectId,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch comparison metrics history");
			}

			const data = (await response.json()) as any;
			return (data as any[]).map((metric) => ({
				...metric,
				measuredAt: new Date(metric.measuredAt),
			})) as ComparisonMetric[];
		},
		enabled: !!propertyId,
		staleTime: 10 * 60 * 1000,
	});

export interface GroundingSearchHistoryFilterOptions
	extends HistoricalMetricsFilterOptions {
	projectId?: string;
}

export const groundingSearchMetricsHistoryQueryKey = (
	propertyId: string,
	onlyGaps?: boolean,
	filters?: GroundingSearchHistoryFilterOptions,
) =>
	[
		"grounding-search-metrics-history",
		propertyId,
		onlyGaps,
		filters?.startDate?.toISOString(),
		filters?.endDate?.toISOString(),
		filters?.projectId,
	] as const;

export const useGroundingSearchMetricsHistoryQuery = (
	propertyId: string,
	onlyGaps?: boolean,
	filters?: GroundingSearchHistoryFilterOptions,
) =>
	useQuery({
		queryKey: groundingSearchMetricsHistoryQueryKey(
			propertyId,
			onlyGaps,
			filters,
		),
		queryFn: async () => {
			const response = await apiClient.properties[":id"][
				"historical-metrics"
			]["grounding-searches"].$get({
				param: { id: propertyId },
				query: {
					...(onlyGaps ? { onlyGaps: "true" } : {}),
					startDate: filters?.startDate?.toISOString(),
					endDate: filters?.endDate?.toISOString(),
					projectId: filters?.projectId,
				},
			});

			if (!response.ok) {
				throw new Error(
					"Failed to fetch grounding search metrics history",
				);
			}

			const data = (await response.json()) as any;
			return (data as any[]).map((metric) => ({
				...metric,
				measuredAt: new Date(metric.measuredAt),
			})) as GroundingSearchMetric[];
		},
		enabled: !!propertyId,
		staleTime: 10 * 60 * 1000,
	});

// ---------------------------------------------------------------------------
// Answer drill-down ("which prompts drove this number, and what did the AI
// say?"). Client-side mirrors of the @repo/core answer-drilldown types —
// the service file imports the db so its types can't be re-exported through
// @repo/core/client.
// ---------------------------------------------------------------------------

export type FunnelStageFilter = "awareness" | "consideration" | "decision";

export interface AnswerDrilldownExecutionSummary {
	executionId: string;
	platform: string;
	platformKey: string | null;
	model: string;
	completedAt: string | null;
	mentioned: boolean;
	rank: number | null;
	ourCitations: number;
	totalCitations: number;
	/**
	 * The tracked brands this answer names in its summary. Counting these
	 * reproduces the Brands page's numbers exactly — same matcher, same set.
	 */
	brandsNamed: string[];
	/**
	 * Only present when the caller passed `brandName`: did THIS answer name the
	 * focused brand? `null` when the execution produced no answer. Focus is a
	 * lens, never a filter — a prompt that missed it stays in the list.
	 */
	namedFocusedBrand?: boolean | null;
}

export interface AnswerDrilldownPrompt {
	queryId: string;
	query: string;
	queryType: string | null;
	stage: FunnelStageFilter;
	tags: string[];
	mentionedAnywhere: boolean;
	executions: AnswerDrilldownExecutionSummary[];
}

/** One tracked brand's totals across the slice the drill-down describes. */
export interface AnswerDrilldownBrandTotal {
	competitorName: string;
	answersNamedIn: number;
	promptsNamedIn: number;
	/** 0-100, one decimal. Null when the slice has no answers. */
	shareOfAnswers: number | null;
}

/** The focused-brand lens summary. */
export interface AnswerDrilldownBrandFocus {
	requestedName: string;
	/** The canonical tracked-brand name it resolved to, or null if untracked. */
	resolvedName: string | null;
	tracked: boolean;
	answersNamedIn: number;
	promptsNamedIn: number;
	shareOfAnswers: number | null;
}

export interface AnswerDrilldownResult {
	date: string;
	source: "nightly" | "report";
	reportId: string | null;
	stage: FunnelStageFilter | null;
	platform: string;
	effectivePlatform: string;
	tag: string | null;
	totals: { mentions: number; opportunities: number; sov: number };
	prompts: AnswerDrilldownPrompt[];
	/**
	 * False when the property has no focal brand (CATEGORY). `totals.mentions`
	 * and every execution's `mentioned` are then computed against nothing, so a
	 * surface must not print "N of M answers mentioned you" — read
	 * `brandTotals` instead.
	 */
	hasFocalSubject: boolean;
	/** Per tracked brand, over exactly this slice. Ranked, highest first. */
	brandTotals: AnswerDrilldownBrandTotal[];
	/** Present only when `brandName` was passed. */
	focusedBrand: AnswerDrilldownBrandFocus | null;
}

export interface AnswerDetailExecution {
	id: string;
	platform: string;
	model: string;
	browsing: boolean;
	answerSummary: string;
	ourBrandInSummary: boolean;
	ourBrandRankInSummary: number | null;
	totalCitations: number;
	ourDomainCitations: number;
	completedAt: string | null;
	error: string | null;
	citations: Array<{
		id: string;
		url: string;
		title: string | null;
		description: string | null;
		isOurDomain: boolean;
		brandAttributed: string | null;
	}>;
	brandMentions: Array<{
		id: string;
		brandName: string;
		isOurBrand: boolean;
		isCompetitor: boolean;
		inSummary: boolean;
		summaryRank: number | null;
		context: string | null;
	}>;
	/** The tracked brands this answer names in its summary — see the L1 type. */
	brandsNamed: string[];
	googleAiOverview?: unknown;
	groundingSearches: Array<{
		id: string;
		query: string;
		orderInResponse: number | null;
		searchContext: string | null;
		rankings: Array<{
			id: string;
			domain: string;
			isOurBrand: boolean;
			isCompetitor: boolean;
			rankPosition: number | null;
			url: string | null;
			title: string | null;
			description: string | null;
		}>;
	}>;
}

export interface AnswerDetailResult {
	date: string;
	source: "nightly" | "report";
	queryId: string;
	query: string;
	queryType: string | null;
	stage: FunnelStageFilter;
	tags: string[];
	executions: AnswerDetailExecution[];
	brandSnapshot: { data: unknown } | null;
	propertyAliases: string[];
	competitorNames: string[];
	propertyCompetitors: Array<{
		url: string;
		name: string | null;
		aliases?: string[];
	}>;
	domain: string | null;
	/**
	 * False when the property has no focal brand (CATEGORY). Passed straight to
	 * the answer renderer so it drops every focal-brand reading instead of
	 * computing one against the market's own name.
	 */
	hasFocalSubject: boolean;
}

export interface StageAnswersQueryOptions {
	date: string;
	stage?: FunnelStageFilter;
	platform?: string;
	tag?: string | null;
	projectId?: string | null;
	/** Scope to one LOCATION child's runs (Plan 5). Omit for brand-wide. */
	locationPropertyId?: string | null;
	/**
	 * Put one tracked brand in focus. A LENS, never a filter: the prompt list
	 * is identical with and without it, and every execution gains a
	 * named / not named verdict for that brand.
	 */
	brandName?: string | null;
}

export const stageAnswersQueryKey = (
	propertyId: string,
	options: StageAnswersQueryOptions,
) =>
	[
		"historical-metrics-answers",
		propertyId,
		options.date,
		options.stage ?? null,
		options.platform ?? "all",
		options.tag ?? null,
		options.projectId ?? null,
		options.locationPropertyId ?? null,
		options.brandName ?? null,
	] as const;

/**
 * Level 1 of the drill-down: prompts + per-platform mention status for one
 * UTC day. `enabled` is gated by the caller (fetch only while the modal is
 * open). A 404 (no metric row for the day) resolves to `null` so the modal
 * can render its empty state instead of an error.
 */
export const useStageAnswersQuery = (
	propertyId: string,
	options: StageAnswersQueryOptions,
	enabled: boolean,
) =>
	useQuery({
		queryKey: stageAnswersQueryKey(propertyId, options),
		queryFn: async (): Promise<AnswerDrilldownResult | null> => {
			const response = await (apiClient.properties as any)[":id"][
				"historical-metrics"
			].answers.$get({
				param: { id: propertyId },
				query: {
					date: options.date,
					...(options.stage ? { stage: options.stage } : {}),
					...(options.platform && options.platform !== "all"
						? { platform: options.platform }
						: {}),
					...(options.tag ? { tag: options.tag } : {}),
					...(options.projectId
						? { projectId: options.projectId }
						: {}),
					...(options.locationPropertyId
						? { locationPropertyId: options.locationPropertyId }
						: {}),
					...(options.brandName
						? { brandName: options.brandName }
						: {}),
				},
			});

			if (response.status === 404) {
				return null;
			}
			if (!response.ok) {
				throw new Error("Failed to fetch answer drill-down");
			}

			return (await response.json()) as AnswerDrilldownResult;
		},
		enabled: enabled && !!propertyId && !!options.date,
		staleTime: 10 * 60 * 1000,
	});

export const promptAnswerDetailQueryKey = (
	propertyId: string,
	date: string,
	queryId: string,
) => ["historical-metrics-answer-detail", propertyId, date, queryId] as const;

/**
 * Level 2 of the drill-down: full per-platform answers for one prompt on one
 * UTC day. Fetched only when the user opens a prompt (`enabled` gating).
 * Pass `locationPropertyId` when the answers came from a LOCATION child's
 * runs (e.g. a coverage-citation hit carrying one) so the reconstruction
 * scopes to that location instead of the parent brand.
 */
export const usePromptAnswerDetailQuery = (
	propertyId: string,
	date: string | null,
	queryId: string | null,
	enabled: boolean,
	locationPropertyId?: string | null,
) =>
	useQuery({
		queryKey: [
			...promptAnswerDetailQueryKey(
				propertyId,
				date ?? "",
				queryId ?? "",
			),
			locationPropertyId ?? "",
		],
		queryFn: async (): Promise<AnswerDetailResult | null> => {
			const response = await (apiClient.properties as any)[":id"][
				"historical-metrics"
			].answers.detail.$get({
				param: { id: propertyId },
				query: {
					date: date as string,
					queryId: queryId as string,
					...(locationPropertyId ? { locationPropertyId } : {}),
				},
			});

			if (response.status === 404) {
				return null;
			}
			if (!response.ok) {
				throw new Error("Failed to fetch answer detail");
			}

			return (await response.json()) as AnswerDetailResult;
		},
		enabled: enabled && !!propertyId && !!date && !!queryId,
		staleTime: 10 * 60 * 1000,
	});

// Answer record — one stored AI answer, reachable from prompt, grounding
// search or citation.

export interface AnswerRecordCitedPage {
	title: string | null;
	metaDescription: string | null;
	author: string | null;
	publishedAt: string | null;
	wordCount: number | null;
	fetchStatus: string;
	/** Leading slice of the stored page content; null when nothing is stored. */
	excerpt: string | null;
	/** True when the stored content is longer than the excerpt. */
	truncated: boolean;
}

export interface AnswerRecordCitationContext {
	citationId: string;
	url: string;
	domain: string | null;
	title: string | null;
	description: string | null;
	citedPage: AnswerRecordCitedPage | null;
}

export interface AnswerRecordResult {
	/** The execution asked for — its platform tab opens first. */
	executionId: string;
	platform: string;
	source: "nightly" | "report";
	reportId: string | null;
	promptRunId: string | null;
	queryId: string | null;
	query: string;
	queryType: string | null;
	stage: string;
	tags: string[];
	/** Completion time of the parent run/report, ISO. */
	completedAt: string | null;
	/** Target + siblings, shaped for TabbedDiscoveryQuery. */
	executions: AnswerDetailExecution[];
	brandSnapshot: { data: unknown } | null;
	propertyAliases: string[];
	competitorNames: string[];
	propertyCompetitors: Array<{
		url: string;
		name: string | null;
		aliases?: string[];
	}>;
	domain: string | null;
	/** See `AnswerDetailResult.hasFocalSubject`. */
	hasFocalSubject: boolean;
	citationContext: AnswerRecordCitationContext | null;
	/**
	 * Recommendation-category judge output for the executions in
	 * `executions`, in judge order. Focal / tracked status is re-resolved at
	 * read time. Empty for report answers and unassessed answers. Optional so
	 * an older API response (before the judge shipped) still parses.
	 */
	assessments?: RecommendationAssessment[];
	/** Judge status for every execution in `executions`. */
	assessmentStatus?: RecommendationAssessmentStatus[];
	/**
	 * The persona the answer was asked as (a daily persona run); null for
	 * baseline and report answers. `contextText` is the context that was SENT
	 * (the persona may have been edited since). Optional so an older API
	 * response still parses.
	 */
	persona?: AnswerRecordPersona | null;
	/**
	 * The exact text sent to the platforms for a daily run: the persona
	 * context + the unchanged question for a persona answer. Null for report
	 * answers.
	 */
	querySent?: string | null;
}

/** The persona chip on an answer record. */
export interface AnswerRecordPersona {
	id: string;
	/** Role-first label ("CFO, early-stage startup"). */
	label: string;
	/** The persona context as it was sent with the question. */
	contextText: string;
	version: number | null;
	archived: boolean;
}

export const answerRecordQueryKey = (
	propertyId: string,
	executionId: string,
	citationId: string | null,
) => ["answer-record", propertyId, executionId, citationId ?? ""] as const;

/**
 * One routed answer record. A 404 (no such execution, or it belongs to another
 * property — the endpoint deliberately can't tell you which) resolves to
 * `null` so the page renders its not-found card instead of an error.
 */
export const useAnswerRecordQuery = (
	propertyId: string,
	executionId: string,
	citationId?: string | null,
) =>
	useQuery({
		queryKey: answerRecordQueryKey(
			propertyId,
			executionId,
			citationId ?? null,
		),
		queryFn: async (): Promise<AnswerRecordResult | null> => {
			const response = await (apiClient.properties as any)[":id"].answers[
				":executionId"
			].$get({
				param: { id: propertyId, executionId },
				query: citationId ? { citationId } : {},
			});

			if (response.status === 404) {
				return null;
			}
			if (!response.ok) {
				throw new Error("Failed to fetch answer record");
			}

			return (await response.json()) as AnswerRecordResult;
		},
		enabled: !!propertyId && !!executionId,
		staleTime: 10 * 60 * 1000,
	});

// ---------------------------------------------------------------------------
// Recommendation categories (daily tracking only)
// ---------------------------------------------------------------------------

/**
 * The slice every recommendation read takes. Mirrors the query of
 * `GET /properties/:id/historical-metrics/recommendations{,/answers}` (see
 * `packages/api/src/routes/properties/recommendation-schemas.ts`).
 *
 * The persona fields read the answers asked as a persona (or a group of
 * personas sharing an attribute) instead of the baseline. Unset, nothing is
 * sent and the cache key keeps its baseline shape. The API's deprecated
 * `personaId` alias is never sent: `persona` is the one spelling.
 */
export interface RecommendationSliceParams {
	days: RecommendationWindowDays;
	platform?: PlatformKey | null;
	stage?: FunnelStage | null;
	projectId?: string | null;
	tags?: readonly string[] | null;
	/** A LOCATION child's runs; omit for brand-wide. */
	locationPropertyId?: string | null;
	/** One persona id: its daily persona answers instead of the baseline. */
	persona?: string | null;
	/**
	 * With `personaGroup`: every non-archived persona sharing this attribute.
	 * Ignored (and not sent) when `persona` is set, as the API ignores it.
	 */
	personaGroupBy?: Exclude<PersonaLensGroupBy, "persona"> | null;
	/** The attribute value; "__unset__" = personas with no value. */
	personaGroup?: string | null;
}

export interface RecommendationsQueryParams extends RecommendationSliceParams {
	/** Non-focal brands before the tail folds into Other; "all" = no fold. */
	topN?: number | "all" | null;
	trendBrand?: string | null;
	granularity?: "day" | "week" | null;
}

export interface RecommendationAnswersQueryParams
	extends RecommendationSliceParams {
	brand?: string | null;
	category?: RecommendationRowCategory | null;
	reasonType?: RecommendationReasonType | null;
	/** A caveat phrase id: `${reasonType}:${reasonNorm}`. */
	reason?: string | null;
	bestFor?: string | null;
	/** A theme id from `cautionThemes` / `bestForThemes`. */
	theme?: string | null;
	/** Narrows `theme` to one wording (its `phraseNorm`). */
	themePhrase?: string | null;
	/** Cited but not recommended. */
	cnr?: boolean;
	page?: number;
	pageSize?: number;
	includeAnswerText?: boolean;
}

function recommendationSliceQuery(
	params: RecommendationSliceParams,
): Record<string, string> {
	const query: Record<string, string> = { days: String(params.days) };
	if (params.platform) {
		query.platform = params.platform;
	}
	if (params.stage) {
		query.stage = params.stage;
	}
	if (params.projectId) {
		query.projectId = params.projectId;
	}
	if (params.tags && params.tags.length > 0) {
		query.tags = params.tags.join(",");
	}
	if (params.locationPropertyId) {
		query.locationPropertyId = params.locationPropertyId;
	}
	// Only when set, so a baseline request (and its cache key) is unchanged.
	if (params.persona) {
		query.persona = params.persona;
	} else if (params.personaGroupBy && params.personaGroup) {
		query.personaGroupBy = params.personaGroupBy;
		query.personaGroup = params.personaGroup;
	}
	return query;
}

/** Root key of every L2 read for a property: invalidate this after "Track". */
export const recommendationsQueryRootKey = (propertyId: string) =>
	["recommendations", propertyId] as const;

export const recommendationsQueryKey = (
	propertyId: string,
	params: RecommendationsQueryParams,
) =>
	[
		...recommendationsQueryRootKey(propertyId),
		recommendationSliceQuery(params),
		params.topN ?? null,
		params.trendBrand ?? null,
		params.granularity ?? null,
	] as const;

/**
 * The recommendations L2 payload (also feeds the L1 card and the SoR columns
 * on the Brands / Competitors L2). A 404 resolves to `null`.
 */
export const useRecommendationsQuery = (
	propertyId: string,
	params: RecommendationsQueryParams,
	enabled = true,
) =>
	useQuery({
		queryKey: recommendationsQueryKey(propertyId, params),
		queryFn: async (): Promise<RecommendationsResponse | null> => {
			const query: Record<string, string> =
				recommendationSliceQuery(params);
			if (params.topN != null) {
				query.topN = String(params.topN);
			}
			if (params.trendBrand) {
				query.trendBrand = params.trendBrand;
			}
			if (params.granularity) {
				query.granularity = params.granularity;
			}
			const response = await (apiClient.properties as any)[":id"][
				"historical-metrics"
			].recommendations.$get({
				param: { id: propertyId },
				query,
			});
			if (response.status === 404) {
				return null;
			}
			if (!response.ok) {
				throw new Error("Failed to fetch recommendation categories");
			}
			return (await response.json()) as RecommendationsResponse;
		},
		enabled: enabled && !!propertyId,
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

/** Root key of every L3 read for a property. */
export const recommendationAnswersQueryRootKey = (propertyId: string) =>
	["recommendation-answers", propertyId] as const;

function recommendationAnswersQuery(
	params: RecommendationAnswersQueryParams,
): Record<string, string> {
	const query = recommendationSliceQuery(params);
	if (params.brand) {
		query.brand = params.brand;
	}
	if (params.category) {
		query.category = params.category;
	}
	if (params.reasonType) {
		query.reasonType = params.reasonType;
	}
	if (params.reason) {
		query.reason = params.reason;
	}
	if (params.bestFor) {
		query.bestFor = params.bestFor;
	}
	if (params.theme) {
		query.theme = params.theme;
		if (params.themePhrase) {
			query.themePhrase = params.themePhrase;
		}
	}
	if (params.cnr) {
		query.cnr = "1";
	}
	if (params.page && params.page > 1) {
		query.page = String(params.page);
	}
	if (params.pageSize) {
		query.pageSize = String(params.pageSize);
	}
	if (params.includeAnswerText) {
		query.includeAnswerText = "1";
	}
	return query;
}

export const recommendationAnswersQueryKey = (
	propertyId: string,
	params: RecommendationAnswersQueryParams,
) =>
	[
		...recommendationAnswersQueryRootKey(propertyId),
		recommendationAnswersQuery(params),
	] as const;

/**
 * One page of L3 rows. Plain function so the CSV export can page through
 * every row (with `includeAnswerText`) outside react-query.
 */
export async function fetchRecommendationAnswers(
	propertyId: string,
	params: RecommendationAnswersQueryParams,
): Promise<RecommendationAnswersPage | null> {
	const response = await (apiClient.properties as any)[":id"][
		"historical-metrics"
	].recommendations.answers.$get({
		param: { id: propertyId },
		query: recommendationAnswersQuery(params),
	});
	if (response.status === 404) {
		return null;
	}
	if (!response.ok) {
		throw new Error("Failed to fetch recommendation answers");
	}
	return (await response.json()) as RecommendationAnswersPage;
}

export const useRecommendationAnswersQuery = (
	propertyId: string,
	params: RecommendationAnswersQueryParams,
	enabled = true,
) =>
	useQuery({
		queryKey: recommendationAnswersQueryKey(propertyId, params),
		queryFn: () => fetchRecommendationAnswers(propertyId, params),
		enabled: enabled && !!propertyId,
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

// ---------------------------------------------------------------------------
// Persona lens ("Perspectives" L2)
// ---------------------------------------------------------------------------

/**
 * The query of `GET /properties/:id/historical-metrics/personas` (see
 * `packages/api/src/routes/properties/persona-lens-schemas.ts`). Google AI
 * Overviews never has persona answers, so it is not a valid `platform`.
 */
export interface PersonaLensQueryParams {
	days: PersonaLensWindowDays;
	platform?: Exclude<PlatformKey, "googleAiOverview"> | null;
	projectId?: string | null;
	/** Default "persona" (one row per persona). */
	groupBy?: PersonaLensGroupBy | null;
	/** Include archived personas (default: they are listed, not measured). */
	includeArchived?: boolean;
}

function personaLensQuery(
	params: PersonaLensQueryParams,
): Record<string, string> {
	const query: Record<string, string> = { days: String(params.days) };
	if (params.platform) {
		query.platform = params.platform;
	}
	if (params.projectId) {
		query.projectId = params.projectId;
	}
	if (params.groupBy && params.groupBy !== "persona") {
		query.groupBy = params.groupBy;
	}
	if (params.includeArchived) {
		query.includeArchived = "1";
	}
	return query;
}

/** Root key of every persona-lens read for a property. */
export const personaLensQueryRootKey = (propertyId: string) =>
	["persona-lens", propertyId] as const;

export const personaLensQueryKey = (
	propertyId: string,
	params: PersonaLensQueryParams,
) =>
	[...personaLensQueryRootKey(propertyId), personaLensQuery(params)] as const;

/**
 * The persona lens: each persona (or group) against the baseline over the
 * same prompts and platforms. A 404 resolves to `null`. Session, API key and
 * portal access, like the recommendations reads.
 */
export const usePersonaLensQuery = (
	propertyId: string,
	params: PersonaLensQueryParams,
	enabled = true,
) =>
	useQuery({
		queryKey: personaLensQueryKey(propertyId, params),
		queryFn: async (): Promise<PersonaLensResponse | null> => {
			const response = await (apiClient.properties as any)[":id"][
				"historical-metrics"
			].personas.$get({
				param: { id: propertyId },
				query: personaLensQuery(params),
			});
			if (response.status === 404) {
				return null;
			}
			if (!response.ok) {
				throw new Error("Failed to fetch the persona lens");
			}
			return (await response.json()) as PersonaLensResponse;
		},
		enabled: enabled && !!propertyId,
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

// Citation Intelligence Types
export interface CitationIntelligenceSummary {
	totalCitations: number;
	classifiedCitations: number;
	classificationRate: number;
	ownedCitations: number;
	thirdPartyCitations: number;
	listicleCitations: number;
	listicleRate: number;
	avgConfidence: number;
	/** Evaluated-vs-cited split ("unclassified" = pre-backfill rows). */
	citationTypeBreakdown: {
		citedInline: number;
		evaluatedSource: number;
		searchResult: number;
		unclassified: number;
	};
	/** Distinct AI answers behind `totalCitations` (the scope-line denominator). */
	answerCount: number;
	earnedMedia: {
		/**
		 * Third-party sources whose fetched content mentions the brand. Always 0
		 * for a CATEGORY, which has no brand of its own.
		 */
		thirdPartySourcesMentioningBrand: number;
		/** Of those, sources never surfaced by answer-text attribution. */
		hiddenEarnedMedia: number;
	};
}

export interface CitationBreakdowns {
	mediaTypes: Record<string, number>;
	authorityTypes: Record<string, number>;
	contentFormats: Record<string, number>;
	pageTypes: Record<string, number>;
}

export interface PlatformCitationStats {
	citations: number;
	mediaTypes: Record<string, number>;
	authorityTypes: Record<string, number>;
}

export interface HistoricalCitationData {
	reportId: string;
	completedAt: Date;
	totalCitations: number;
	ownedCitations: number;
	listicleCitations: number;
	mediaTypes: Record<string, number>;
	authorityTypes: Record<string, number>;
	contentFormats: Record<string, number>;
	shareOfOwned: number;
	listicleRate: number;
}

export interface CitationSource {
	domain: string;
	citations: number;
	mediaTypes: Record<string, number>;
	/** Per-source authority-type counts. */
	authorityTypes: Record<string, number>;
	/** Per-source content-format counts. */
	contentFormats: Record<string, number>;
	/**
	 * Discovery-query types of the prompts that cited this source. A floor,
	 * not an exact set — citations are deduped by url+platform (see the
	 * service). Filtering by `promptTypes` is exact regardless.
	 */
	promptTypes: string[];
	/** Platforms that cited this source. */
	platforms: string[];
	/** Citations of this source per platform; sums to `citations`. */
	platformCitations: Record<string, number>;
	/** ISO timestamp of the most recent run/report that cited this source. */
	lastCitedAt: string | null;
	/**
	 * Per-source counts of which brand each citation was attributed to.
	 * Used to drive the brand-attribution filter on the publisher sources table.
	 * Citations with no brand attribution are counted under the special key
	 * "_unattributed".
	 */
	brandMentions: Record<string, number>;
	/**
	 * Citation-weighted counts of brands mentioned IN the cited pages' fetched
	 * content — the content-derived analogue of brandMentions.
	 */
	contentMentions: Record<string, number>;
	mentionsOurBrand: boolean;
	mentionsCompetitor: boolean;
	urls: Array<{ url: string; title: string | null }>;
	/** "brand" = property's own domain; "competitor" = a tracked competitor; "third_party" = neither. */
	ownerType: "brand" | "competitor" | "third_party";
	/** Display name when ownerType is brand or competitor; null otherwise. */
	ownerName: string | null;
	/**
	 * The user has benched this domain for this property ("never an outreach
	 * target"). Ignored sources are still returned everywhere — the flag only
	 * removes them from earned-media opportunity recommendations (see
	 * `isOpportunitySource`) and blocks them as PR-project publisher goals.
	 */
	ignored: boolean;
}

/** @deprecated Use {@link CitationSource} — third-party-only view will be removed in a future release. */
export type CompetitorSource = CitationSource;

export interface CitationIntelligenceFilters {
	platform?: string;
	contentFormat?: string;
	mediaType?: string;
	authorityType?: string;
	/**
	 * Brand-attribution filter. Special tokens: "_any_brand" (any attributed
	 * brand), "_unattributed" (no attributed brand); otherwise a brand/competitor
	 * name matched case-insensitively (contains) against citation attribution.
	 */
	brandAttributed?: string;
	/** "cited_inline" | "evaluated_source" | "search_result" | "unclassified" | "all". */
	citationType?: string;
	/**
	 * Content-mention filter (from fetched page content): a brand/competitor
	 * name, "_our_brand", "_any_competitor", or "_competitor_not_us" (the
	 * earned-media cut — sources that mention a competitor and never you,
	 * applied per source rather than per citation).
	 */
	mentionedBrand?: string;
	/** Discovery-query types, e.g. ["category_best"]. Empty = every type. */
	promptTypes?: string[];
	/**
	 * One publisher, e.g. "techcrunch.com". Exact hostname match after
	 * normalisation — subdomains stay distinct publishers. Empty/"all" = every
	 * publisher. Applies to the aggregate AND the citation list, so a
	 * publisher-scoped page keeps its KPIs, trend and table describing one set.
	 */
	domain?: string;
	/** Page size for topCitationSources. Omit for the uncapped list. */
	sourcesLimit?: number;
	sourcesOffset?: number;
	startDate?: string;
	endDate?: string;
}

/** A selectable brand-attribution option returned by the citation-intelligence endpoint. */
export interface CitationBrandFilterOption {
	name: string;
	ownerType: "brand" | "competitor";
}

/** The four clickable facet dimensions on the Citation Intelligence page. */
export type CitationFacetDimension =
	| "mediaType"
	| "authorityType"
	| "contentFormat"
	| "platform";

export type CitationFacetCounts = Record<
	CitationFacetDimension,
	Record<string, number>
>;

export interface CitationIntelligenceData {
	reportId: string;
	reportCompletedAt: Date;
	domain: string;
	appliedFilters: {
		platform: string;
		contentFormat: string;
		mediaType: string;
		authorityType: string;
		brandAttributed: string;
		citationType: string;
		mentionedBrand: string;
		promptTypes: string[];
		startDate: string | null;
		endDate: string | null;
	};
	availableFilters: {
		platforms: string[];
		contentFormats: string[];
		mediaTypes: string[];
		authorityTypes: string[];
		brands: CitationBrandFilterOption[];
		citationTypes: string[];
		/** Discovery-query types that produced a citation in range (unfiltered). */
		promptTypes: string[];
	};
	summary: CitationIntelligenceSummary;
	breakdowns: CitationBreakdowns;
	platformStats: Record<string, PlatformCitationStats>;
	/**
	 * Per-dimension counts computed over the citations matching every OTHER
	 * active filter but not the dimension's own — what keeps the four facet
	 * charts composable (clicking "video" must not zero out the other formats).
	 * Full value lists; folding to top-5 + Other is presentation-only.
	 */
	facetCounts: CitationFacetCounts;
	/**
	 * The same tallies with all four dimension filters lifted. Stable while the
	 * user clicks facets, so chart colour slots can be assigned by window volume
	 * without filters repainting the survivors.
	 */
	facetTotals: CitationFacetCounts;
	/**
	 * `facetCounts` in PUBLISHERS: distinct source domains per value over the
	 * same facet-excluded cut. A publisher can sit in several values of one
	 * dimension, so the values can sum past `facetPublisherDistinct`.
	 */
	facetPublisherCounts: CitationFacetCounts;
	/** Distinct publishers in each dimension's facet-excluded cut. */
	facetPublisherDistinct: Record<CitationFacetDimension, number>;
	historicalData: HistoricalCitationData[];
	/** All citation sources (brand + competitor + third-party), ordered by citation count. */
	topCitationSources: CitationSource[];
	/** @deprecated Third-party-only subset retained for backwards compatibility. Use {@link topCitationSources}. */
	topCompetitorSources: CitationSource[];
	/** Source count before sourcesLimit/sourcesOffset are applied. */
	totalSources: number;
	/**
	 * Sites the AI went to DIRECTLY via a `site:domain.com …` search rather than
	 * searching the open web. A citation is a source the model FOUND and chose
	 * to use; a consultation is one it decided to trust before searching, so the
	 * two lists are kept side by side and never merged.
	 */
	consultedSources: ConsultedSource[];
}

/** One directly-consulted site, rolled up over the window. */
export interface ConsultedSource {
	/** Normalized bare hostname. */
	domain: string;
	class: "own" | "competitor" | "third_party";
	count: number;
	platforms: string[];
}

/**
 * Prefix matching EVERY citation-intelligence cache entry for a property,
 * whatever its filters. Invalidate with this after a mutation that changes what
 * the endpoint returns for all windows at once (e.g. ignoring a publisher) —
 * `citationIntelligenceQueryKey(propertyId)` will NOT do, because its trailing
 * `undefined` filter slots fail react-query's element-wise partial match
 * against a key that carries a real startDate.
 */
export const citationIntelligenceQueryPrefix = (propertyId: string) =>
	["citation-intelligence", propertyId] as const;

// Citation Intelligence Query Key and Hook
export const citationIntelligenceQueryKey = (
	propertyId: string,
	filters?: CitationIntelligenceFilters,
) =>
	[
		"citation-intelligence",
		propertyId,
		filters?.platform,
		filters?.contentFormat,
		filters?.mediaType,
		filters?.authorityType,
		filters?.brandAttributed,
		filters?.citationType,
		filters?.mentionedBrand,
		filters?.promptTypes?.join(","),
		filters?.domain,
		filters?.sourcesLimit,
		filters?.sourcesOffset,
		filters?.startDate,
		filters?.endDate,
	] as const;

export const useCitationIntelligenceQuery = (
	propertyId: string,
	filters?: CitationIntelligenceFilters,
	options?: { enabled?: boolean },
) =>
	useQuery({
		queryKey: citationIntelligenceQueryKey(propertyId, filters),
		queryFn: async () => {
			const queryParams: Record<string, string> = {};
			if (filters?.platform && filters.platform !== "all") {
				queryParams.platform = filters.platform;
			}
			if (filters?.contentFormat && filters.contentFormat !== "all") {
				queryParams.contentFormat = filters.contentFormat;
			}
			if (filters?.mediaType && filters.mediaType !== "all") {
				queryParams.mediaType = filters.mediaType;
			}
			if (filters?.authorityType && filters.authorityType !== "all") {
				queryParams.authorityType = filters.authorityType;
			}
			if (filters?.brandAttributed && filters.brandAttributed !== "all") {
				queryParams.brandAttributed = filters.brandAttributed;
			}
			if (filters?.citationType && filters.citationType !== "all") {
				queryParams.citationType = filters.citationType;
			}
			if (filters?.mentionedBrand && filters.mentionedBrand !== "all") {
				queryParams.mentionedBrand = filters.mentionedBrand;
			}
			if (filters?.promptTypes?.length) {
				queryParams.promptTypes = filters.promptTypes.join(",");
			}
			if (filters?.domain && filters.domain !== "all") {
				queryParams.domain = filters.domain;
			}
			if (filters?.sourcesLimit !== undefined) {
				queryParams.sourcesLimit = String(filters.sourcesLimit);
			}
			if (filters?.sourcesOffset !== undefined) {
				queryParams.sourcesOffset = String(filters.sourcesOffset);
			}
			if (filters?.startDate) {
				queryParams.startDate = filters.startDate;
			}
			if (filters?.endDate) {
				queryParams.endDate = filters.endDate;
			}

			const response = await apiClient.properties[":id"][
				"citation-intelligence"
			].$get({
				param: { id: propertyId },
				query: queryParams,
			});

			if (!response.ok) {
				if (response.status === 404) {
					return null; // No citation data available
				}
				throw new Error("Failed to fetch citation intelligence data");
			}

			const data = (await response.json()) as any;
			return {
				...data,
				reportCompletedAt: data.reportCompletedAt
					? new Date(data.reportCompletedAt)
					: null,
				historicalData:
					data.historicalData?.map((h: any) => ({
						...h,
						completedAt: h.completedAt
							? new Date(h.completedAt)
							: null,
					})) || [],
			} as CitationIntelligenceData;
		},
		enabled: !!propertyId && (options?.enabled ?? true),
		staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
	});

// ---------------------------------------------------------------------------
// Publisher Citations (per-domain drilldown for Citation Intelligence dashboard)
// ---------------------------------------------------------------------------

export interface PublisherCitation {
	id: string;
	url: string;
	title: string | null;
	description: string | null;
	isOurDomain: boolean;
	brandAttributed: string | null;
	pageType: string | null;
	contentFormat: string | null;
	mediaType: string | null;
	authorityType: string | null;
	isListicle: boolean;
	urlDepth: number | null;
	classificationMethod: string | null;
	classificationConfidence: number | null;
	classifiedAt: string | null;
	citationType: string | null;
	author: string | null;
	publishedAt: string | null;
	platform: string;
	queryText: string | null;
	/**
	 * When the AI answer that carried this citation completed (ISO), or null
	 * when the execution never recorded one.
	 */
	citedAt: string | null;
	/**
	 * Brands found IN this page's fetched content (from CitedPageBrandMention).
	 * Sorted our-brand first, then competitors, then by mention volume. Lets a
	 * per-page row flag which cited pages actually mention your brand — distinct
	 * from `brandAttributed`, which is who the AI's answer credited the citation to.
	 */
	contentMentions?: Array<{
		brandName: string;
		isOurBrand: boolean;
		isCompetitor: boolean;
		mentionCount: number;
	}>;
}

export interface PublisherCitationsResponse {
	domain: string;
	citations: PublisherCitation[];
	total: number;
}

export const publisherCitationsQueryKey = (
	propertyId: string,
	domain: string,
	filters?: CitationIntelligenceFilters,
) =>
	[
		"publisher-citations",
		propertyId,
		domain,
		filters?.platform,
		filters?.contentFormat,
		filters?.mediaType,
		filters?.authorityType,
		filters?.brandAttributed,
		filters?.citationType,
		filters?.mentionedBrand,
		filters?.startDate,
		filters?.endDate,
	] as const;

export const usePublisherCitationsQuery = (
	propertyId: string,
	domain: string | null,
	filters: CitationIntelligenceFilters | undefined,
	enabled: boolean,
) =>
	useQuery({
		queryKey: publisherCitationsQueryKey(propertyId, domain ?? "", filters),
		queryFn: async () => {
			// `enabled` gates execution on a non-null domain; this guard makes the
			// invariant explicit instead of asserting it away at the call site.
			if (!domain) {
				throw new Error("publisher citations query requires a domain");
			}
			const queryParams: Record<string, string> = {};
			if (filters?.platform && filters.platform !== "all") {
				queryParams.platform = filters.platform;
			}
			if (filters?.contentFormat && filters.contentFormat !== "all") {
				queryParams.contentFormat = filters.contentFormat;
			}
			if (filters?.mediaType && filters.mediaType !== "all") {
				queryParams.mediaType = filters.mediaType;
			}
			if (filters?.authorityType && filters.authorityType !== "all") {
				queryParams.authorityType = filters.authorityType;
			}
			if (filters?.brandAttributed && filters.brandAttributed !== "all") {
				queryParams.brandAttributed = filters.brandAttributed;
			}
			if (filters?.citationType && filters.citationType !== "all") {
				queryParams.citationType = filters.citationType;
			}
			if (filters?.mentionedBrand && filters.mentionedBrand !== "all") {
				queryParams.mentionedBrand = filters.mentionedBrand;
			}
			if (filters?.startDate) queryParams.startDate = filters.startDate;
			if (filters?.endDate) queryParams.endDate = filters.endDate;

			const response = await apiClient.properties[":id"][
				"citation-intelligence"
			].sources[":domain"].citations.$get({
				param: { id: propertyId, domain: encodeURIComponent(domain) },
				query: queryParams,
			});

			if (!response.ok) {
				throw new Error("Failed to fetch publisher citations");
			}

			return (await response.json()) as PublisherCitationsResponse;
		},
		enabled: enabled && !!propertyId && !!domain,
		staleTime: 5 * 60 * 1000,
	});

// ---------------------------------------------------------------------------
// Citation list — the L3 grain behind the Citation Intelligence page. One row
// per observed citation, paginated on the server.
// ---------------------------------------------------------------------------

export interface CitationRow {
	/** Row key: `<citationId>::<platform>`. Not a database id. */
	id: string;
	/** The DiscoveryCitation primary key — what deep links carry. */
	citationId: string;
	/** The execution whose answer carried this citation; null when unknown. */
	executionId: string | null;
	/** ISO timestamp of the report/run that observed the citation. */
	citedAt: string | null;
	url: string;
	domain: string;
	title: string | null;
	description: string | null;
	platform: string;
	/** The tracked prompt whose answer carried this citation. */
	promptText: string | null;
	promptType: string | null;
	mediaType: string | null;
	authorityType: string | null;
	contentFormat: string | null;
	pageType: string | null;
	isListicle: boolean;
	citationType: string | null;
	brandAttributed: string | null;
	ownerType: "brand" | "competitor" | "third_party";
	ownerName: string | null;
	citedPageId: string | null;
	mentionsOurBrand: boolean;
	mentionsCompetitor: boolean;
	contentMentions: Array<{
		brandName: string;
		isOurBrand: boolean;
		isCompetitor: boolean;
		mentionCount: number;
	}>;
	/** Excerpt of the answer this citation appeared in; page rows only. */
	answerSnippet: string | null;
	/** True when the excerpt is the passage naming this source, not the opening. */
	answerSnippetMatched: boolean;
}

export interface CitationListResponse {
	citations: CitationRow[];
	total: number;
	/** 1-indexed. */
	page: number;
	pageSize: number;
	totalPages: number;
}

export interface CitationListFilters extends CitationIntelligenceFilters {
	page?: number;
	pageSize?: number;
}

export const citationListQueryKey = (
	propertyId: string,
	filters?: CitationListFilters,
) =>
	[
		"citation-list",
		propertyId,
		filters?.platform,
		filters?.contentFormat,
		filters?.mediaType,
		filters?.authorityType,
		filters?.brandAttributed,
		filters?.citationType,
		filters?.mentionedBrand,
		filters?.promptTypes?.join(","),
		filters?.domain,
		filters?.page,
		filters?.pageSize,
		filters?.startDate,
		filters?.endDate,
	] as const;

export const useCitationListQuery = (
	propertyId: string,
	filters?: CitationListFilters,
	options?: { enabled?: boolean },
) =>
	useQuery({
		queryKey: citationListQueryKey(propertyId, filters),
		queryFn: async () => {
			const queryParams: Record<string, string> = {};
			if (filters?.platform && filters.platform !== "all") {
				queryParams.platform = filters.platform;
			}
			if (filters?.contentFormat && filters.contentFormat !== "all") {
				queryParams.contentFormat = filters.contentFormat;
			}
			if (filters?.mediaType && filters.mediaType !== "all") {
				queryParams.mediaType = filters.mediaType;
			}
			if (filters?.authorityType && filters.authorityType !== "all") {
				queryParams.authorityType = filters.authorityType;
			}
			if (filters?.brandAttributed && filters.brandAttributed !== "all") {
				queryParams.brandAttributed = filters.brandAttributed;
			}
			if (filters?.citationType && filters.citationType !== "all") {
				queryParams.citationType = filters.citationType;
			}
			if (filters?.mentionedBrand && filters.mentionedBrand !== "all") {
				queryParams.mentionedBrand = filters.mentionedBrand;
			}
			if (filters?.promptTypes?.length) {
				queryParams.promptTypes = filters.promptTypes.join(",");
			}
			if (filters?.domain && filters.domain !== "all") {
				queryParams.domain = filters.domain;
			}
			if (filters?.page !== undefined) {
				queryParams.page = String(filters.page);
			}
			if (filters?.pageSize !== undefined) {
				queryParams.pageSize = String(filters.pageSize);
			}
			if (filters?.startDate) {
				queryParams.startDate = filters.startDate;
			}
			if (filters?.endDate) {
				queryParams.endDate = filters.endDate;
			}

			const response = await apiClient.properties[":id"][
				"citation-intelligence"
			].citations.$get({
				param: { id: propertyId },
				query: queryParams,
			});

			if (!response.ok) {
				throw new Error("Failed to fetch citations");
			}

			return (await response.json()) as CitationListResponse;
		},
		enabled: !!propertyId && (options?.enabled ?? true),
		// Keep the previous page on screen while the next one loads, so paging
		// doesn't flash an empty table.
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

// ---------------------------------------------------------------------------
// SEO opportunities (brand-dashboard L2 + the per-query run-history L3).
// Client-side mirrors of the @repo/core seo-opportunities payloads — that
// service imports the db, so its types can't be re-exported through
// @repo/core/client. `RankBand` itself is pure and does come from there.
// ---------------------------------------------------------------------------

export interface SeoBestCompetitor {
	competitorId: string | null;
	name: string;
	domain: string;
	rank: number | null;
	url: string | null;
}

export interface SeoQueryRow {
	groundingSearchId: string;
	searchQuery: string;
	usageCount: number;
	platforms: string[];
	impactScore: number;
	/** Best of Google/Brave; the L3 breaks the engines out. */
	brandRank: number | null;
	band: RankBand;
	previousBrandRank: number | null;
	previousBand: RankBand | null;
	/** Positive = improved by that many bands. Null with no previous run. */
	movement: number | null;
	isGap: boolean;
	bestCompetitor: SeoBestCompetitor | null;
}

export interface SeoMoverRow {
	groundingSearchId: string;
	searchQuery: string;
	band: RankBand;
	previousBand: RankBand;
	brandRank: number | null;
	previousBrandRank: number | null;
	impactScore: number;
}

export interface SeoBandDistributionPoint {
	date: string;
	top3: number;
	top10: number;
	top20: number;
	top30: number;
	unranked: number;
}

export interface SeoOpportunitiesResponse {
	summary: {
		totalSearches: number;
		gapOpportunities: number;
		gapsDelta: number | null;
		highImpactGaps: number;
		movers: number;
		avgImpactScore: number | null;
		latestRunAt: string | null;
	};
	bandDistribution: SeoBandDistributionPoint[];
	movers: SeoMoverRow[];
	competitors: Array<{ id: string; name: string; domain: string }>;
	queries: SeoQueryRow[];
	total: number;
	/** 1-indexed, clamped server-side to the last page with rows. */
	page: number;
	pageSize: number;
	totalPages: number;
}

export interface SeoOpportunitiesParams {
	days?: number;
	search?: string;
	status?: "all" | "gaps" | "ranked";
	band?: RankBand;
	platform?: string;
	competitorId?: string;
	sort?: "impact" | "movement" | "usage";
	page?: number;
	pageSize?: number;
}

export const seoOpportunitiesQueryKey = (
	propertyId: string,
	params?: SeoOpportunitiesParams,
) =>
	[
		"seo-opportunities",
		propertyId,
		params?.days,
		params?.search,
		params?.status,
		params?.band,
		params?.platform,
		params?.competitorId,
		params?.sort,
		params?.page,
		params?.pageSize,
	] as const;

export const useSeoOpportunitiesQuery = (
	propertyId: string,
	params?: SeoOpportunitiesParams,
) =>
	useQuery({
		queryKey: seoOpportunitiesQueryKey(propertyId, params),
		queryFn: async () => {
			const query: Record<string, string> = {};
			if (params?.days !== undefined) {
				query.days = String(params.days);
			}
			if (params?.search) {
				query.search = params.search;
			}
			if (params?.status && params.status !== "all") {
				query.status = params.status;
			}
			if (params?.band) {
				query.band = params.band;
			}
			if (params?.platform) {
				query.platform = params.platform;
			}
			if (params?.competitorId) {
				query.competitorId = params.competitorId;
			}
			if (params?.sort) {
				query.sort = params.sort;
			}
			if (params?.page !== undefined) {
				query.page = String(params.page);
			}
			if (params?.pageSize !== undefined) {
				query.pageSize = String(params.pageSize);
			}

			const response = await apiClient.properties[":id"][
				"seo-opportunities"
			].$get({
				param: { id: propertyId },
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to fetch SEO opportunities");
			}

			return (await response.json()) as SeoOpportunitiesResponse;
		},
		enabled: !!propertyId,
		// Keep the current page on screen while the next one loads, so paging
		// and filter changes don't blank the table.
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

export interface SeoQueryRunRow {
	date: string;
	source: string;
	brandRank: number | null;
	band: RankBand;
	isGap: boolean;
	bestCompetitorRank: number | null;
	bestCompetitorDomain: string | null;
	brandUrl: string | null;
	/** Deep-links to /answers/:executionId when the run's answer is stored. */
	executionId: string | null;
}

export interface SeoQueryRunsResponse {
	query: {
		groundingSearchId: string;
		searchQuery: string;
		usageCount: number;
		impactScore: number;
		platforms: string[];
		discoveryQueries: Array<{ id: string; query: string }>;
		/** False: no trusted rank data at all; show "not measured", not unranked. */
		measured: boolean;
		isGap: boolean;
		brandRank: number | null;
		band: RankBand;
		previousBrandRank: number | null;
		previousBand: RankBand | null;
		bestCompetitor: SeoBestCompetitor | null;
		competitorStandings: Array<{
			competitorId: string;
			name: string;
			domain: string;
			rankGoogle: number | null;
			urlGoogle: string | null;
			rankBrave: number | null;
			urlBrave: string | null;
		}>;
	};
	/** Oldest → newest, ready to chart. */
	series: Array<{ date: string; brandRank: number | null; band: RankBand }>;
	runs: SeoQueryRunRow[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export const seoQueryRunsQueryKey = (
	propertyId: string,
	groundingSearchId: string,
	page?: number,
	pageSize?: number,
) => ["seo-query-runs", propertyId, groundingSearchId, page, pageSize] as const;

export const useSeoQueryRunsQuery = (
	propertyId: string,
	groundingSearchId: string,
	page?: number,
	pageSize?: number,
) =>
	useQuery({
		queryKey: seoQueryRunsQueryKey(
			propertyId,
			groundingSearchId,
			page,
			pageSize,
		),
		queryFn: async () => {
			const query: Record<string, string> = {};
			if (page !== undefined) {
				query.page = String(page);
			}
			if (pageSize !== undefined) {
				query.pageSize = String(pageSize);
			}

			const response = await apiClient.properties[":id"][
				"seo-opportunities"
			].queries[":groundingSearchId"].runs.$get({
				param: { id: propertyId, groundingSearchId },
				query,
			});

			// 404 is "no such search, or another property's" — a state the view
			// renders, not an error to retry.
			if (response.status === 404) {
				return null;
			}
			if (!response.ok) {
				throw new Error("Failed to fetch grounding query runs");
			}

			return (await response.json()) as SeoQueryRunsResponse;
		},
		enabled: !!propertyId && !!groundingSearchId,
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

/** Mirrors `ConsistencyFieldScore` from @repo/core (server-only module). */
export interface ConsistencyFieldScore {
	/** 0–1. */
	score: number;
	details: string;
	matchType: string;
	semanticDetails?: {
		matches: Array<{
			snapshot_item: string;
			llm_item: string;
			similarity_score: number;
			reasoning: string;
		}>;
		unmatched_snapshot: string[];
		unmatched_llm: string[];
		notes: string[];
	} | null;
}

/** Mirrors `ConsistencyScoreShape` from @repo/core. */
export interface ConsistencyScoreDetail {
	/** 0–100. */
	overall: number;
	fieldScores: Record<string, ConsistencyFieldScore>;
	penalties: Array<{
		type: string;
		field: string;
		detail: string;
		penalty: number;
	}>;
	notes: string[];
}

/** The structured brand answer each platform returns, as stored. */
export interface ConsistencyLlmResponse {
	what?: string;
	who_for?: string;
	helps_with?: string[];
	features?: string[];
	differentiators?: string[];
	competitors?: Array<{ name?: string; domain?: string }>;
	pricing_model?: {
		type?: string;
		confidence?: string;
		reasoning?: string;
	};
}

export interface ConsistencyCheckMode {
	browsing: boolean;
	score: number | null;
	llmResponse: ConsistencyLlmResponse | null;
	consistencyScore: ConsistencyScoreDetail | null;
}

export interface ConsistencyCheckPlatform {
	/** Canonical LLM key: openai | gemini | perplexity | claude. */
	platform: string;
	displayName: string;
	model: string | null;
	score: number | null;
	modes: ConsistencyCheckMode[];
}

/** Mirrors `ConsistencyCheckIdentityResult` from @repo/core. */
export interface ConsistencyCheckIdentityResult {
	/** 0–100. */
	accuracyScore: number | null;
	roleAccurate: boolean | null;
	employerAccurate: boolean | null;
	factualErrors: string[];
	/** The answer described a DIFFERENT person who shares the name. */
	confusionDetected: boolean;
	confusedWith: string | null;
	/** Once-correct facts that have since gone stale (not an error). */
	recencyStale: boolean;
	summary: string | null;
}

export interface ConsistencyCheckIdentityMode {
	browsing: boolean;
	score: number | null;
	/** The model's prose answer, verbatim — what the judge scored. */
	responseText: string | null;
	result: ConsistencyCheckIdentityResult | null;
}

export interface ConsistencyCheckIdentityPlatform {
	/** Canonical LLM key: openai | gemini | perplexity | claude. */
	platform: string;
	displayName: string;
	model: string | null;
	score: number | null;
	modes: ConsistencyCheckIdentityMode[];
}

interface ConsistencyCheckDetailBase {
	checkId: string;
	source: string;
	measuredAt: Date;
	overallScore: number;
	/** False for checks recorded before detailed output was stored. */
	detailAvailable: boolean;
}

/** A BRAND subject's check: five scored fields against the brand snapshot. */
export interface BrandConsistencyCheckDetail
	extends ConsistencyCheckDetailBase {
	kind: "brand";
	platforms: ConsistencyCheckPlatform[];
	brandSnapshot: Record<string, unknown> | null;
}

/**
 * A PERSON subject's check: identity accuracy against neutral primary sources.
 * No five scored fields and no brand snapshot exist here — rendering it through
 * the brand shape would show bars that were never scored.
 */
export interface IdentityConsistencyCheckDetail
	extends ConsistencyCheckDetailBase {
	kind: "identity";
	platforms: ConsistencyCheckIdentityPlatform[];
	personSnapshot: Record<string, unknown> | null;
	/** 0–100 agreement across models. */
	crossModelConsistency: number | null;
	summary: string | null;
}

export type ConsistencyCheckDetail =
	| BrandConsistencyCheckDetail
	| IdentityConsistencyCheckDetail;

export const consistencyCheckQueryKey = (propertyId: string, checkId: string) =>
	["consistency-check", propertyId, checkId] as const;

export const useConsistencyCheckQuery = (propertyId: string, checkId: string) =>
	useQuery({
		queryKey: consistencyCheckQueryKey(propertyId, checkId),
		queryFn: async () => {
			const response = await (apiClient.properties as any)[":id"][
				"consistency-checks"
			][":checkId"].$get({
				param: { id: propertyId, checkId },
			});

			// 404 is "no such check, or another property's" — a state the view
			// renders, not an error to retry.
			if (response.status === 404) {
				return null;
			}
			if (!response.ok) {
				throw new Error("Failed to fetch consistency check");
			}

			const data = (await response.json()) as any;
			return {
				...data,
				measuredAt: new Date(data.measuredAt),
			} as ConsistencyCheckDetail;
		},
		enabled: !!propertyId && !!checkId,
		staleTime: 10 * 60 * 1000,
	});

// ---------------------------------------------------------------------------
// Identity accuracy (person dashboard L1)
// ---------------------------------------------------------------------------

export interface IdentityAccuracyPlatform {
	/** Canonical LLM key: openai | gemini | perplexity | claude. */
	platform: string;
	displayName: string;
	/** 0-100, averaged across the browsing modes that answered. */
	score: number;
	modes: number;
}

export interface IdentityAccuracy {
	overallScore: number | null;
	/** Report-over-report change in points; null with only one measurement. */
	delta: number | null;
	perPlatform: IdentityAccuracyPlatform[];
	/** Models that described a different person with the same name. */
	confusions: number;
	/** Models whose answer carried once-correct but stale facts. */
	outdatedFacts: number;
	crossModelConsistency: number | null;
	measuredAt: Date | null;
	reportId: string | null;
}

export const identityAccuracyQueryKey = (propertyId: string) =>
	["identity-accuracy", propertyId] as const;

export const useIdentityAccuracyQuery = (propertyId: string) =>
	useQuery({
		queryKey: identityAccuracyQueryKey(propertyId),
		queryFn: async () => {
			const response = await (apiClient.properties as any)[":id"][
				"identity-accuracy"
			].$get({ param: { id: propertyId } });

			if (!response.ok) {
				throw new Error("Failed to fetch identity accuracy");
			}

			// `null` is the "no report has produced an identity analysis yet"
			// state the card renders — not an error.
			const data = (await response.json()) as any;
			if (!data) {
				return null;
			}
			return {
				...data,
				measuredAt: data.measuredAt ? new Date(data.measuredAt) : null,
			} as IdentityAccuracy;
		},
		enabled: !!propertyId,
		staleTime: 10 * 60 * 1000,
	});

// ---------------------------------------------------------------------------
// Agency white-label client portal — one property's portal site
// ---------------------------------------------------------------------------

/**
 * What the property settings tab renders off.
 *
 * `eligible` is the API's explanation of why the portal cannot be published
 * yet, and is populated even on a lapsed plan: an agency whose subscription
 * ended still has to see the URL it handed its client. That is why GET is the
 * one portal route with no plan gate, and why this query never treats a
 * blocked state as a failure.
 */
export interface PropertyPortalQueryResult {
	site: PortalSiteDto | null;
	domain: PortalDomainDto | null;
	url: string | null;
	eligible: {
		plan: boolean;
		domainActive: boolean;
		subject: boolean;
	};
}

/**
 * Hono's RPC client types a handler's body as the union of every branch it can
 * return, so a caller that has already narrowed by status has to assert the
 * branch it is holding.
 */
async function readPortalSiteJson<T>(response: {
	json: () => Promise<unknown>;
}): Promise<T> {
	return (await response.json()) as T;
}

/** Turn a non-2xx into an error carrying the API's `error` code. */
async function throwPortalSiteError(
	response: { status: number; json: () => Promise<unknown> },
	fallbackMessage: string,
): Promise<never> {
	let code = "UNKNOWN";
	let message = fallbackMessage;

	try {
		const body = await readPortalSiteJson<{
			error?: string;
			message?: string;
		}>(response);
		code = body.error ?? code;
		message = body.message ?? message;
	} catch {
		// A 502 from an edge/proxy layer need not carry JSON at all.
	}

	throw new PortalSiteRequestError(code, message, response.status);
}

export const propertyPortalQueryKey = (propertyId: string) =>
	["property", propertyId, "portal"] as const;

export const usePropertyPortalQuery = (
	propertyId: string | undefined,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: propertyPortalQueryKey(propertyId || ""),
		queryFn: async (): Promise<PropertyPortalQueryResult> => {
			if (!propertyId) {
				throw new Error("Property ID is required");
			}

			const response = await apiClient.properties[":id"].portal.$get({
				param: { id: propertyId },
			});

			if (!response.ok) {
				return await throwPortalSiteError(
					response,
					"Failed to load the client portal",
				);
			}

			return await readPortalSiteJson<PropertyPortalQueryResult>(
				response,
			);
		},
		enabled: !!propertyId && (options?.enabled ?? true),
	});
};

export const updatePropertyPortalMutationKey = [
	"update-property-portal",
] as const;

/**
 * Create or update the portal. The first call with `enabled: true` is also the
 * one that creates the row — the service picks a slug and generates a password
 * for it, so the tab sends no credential of its own.
 */
export const useUpdatePropertyPortalMutation = (propertyId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: updatePropertyPortalMutationKey,
		mutationFn: async (input: {
			enabled?: boolean;
			slug?: string;
			password?: string;
		}) => {
			const response = await apiClient.properties[":id"].portal.$put({
				param: { id: propertyId },
				json: input,
			});

			if (!response.ok) {
				return await throwPortalSiteError(
					response,
					"Failed to save the client portal",
				);
			}

			return await readPortalSiteJson<{
				site: PortalSiteDto;
				url: string;
			}>(response);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: propertyPortalQueryKey(propertyId),
			});
		},
	});
};

export const revealPortalPasswordMutationKey = [
	"reveal-portal-password",
] as const;

/**
 * Reveal the current password.
 *
 * A mutation rather than a query on purpose: revealing a credential is an
 * explicit act, and modelling it as a query would let React Query prefetch it,
 * cache it, and refetch it on window focus — putting a plaintext password in
 * memory nobody asked for.
 */
export const useRevealPortalPasswordMutation = (propertyId: string) => {
	return useMutation({
		mutationKey: revealPortalPasswordMutationKey,
		mutationFn: async () => {
			const response = await apiClient.properties[
				":id"
			].portal.password.$get({ param: { id: propertyId } });

			if (!response.ok) {
				return await throwPortalSiteError(
					response,
					"Failed to reveal the portal password",
				);
			}

			const body = await readPortalSiteJson<{ password: string }>(
				response,
			);

			return body.password;
		},
	});
};

export const regeneratePortalPasswordMutationKey = [
	"regenerate-portal-password",
] as const;

/**
 * Issue a new password — generated, or the one the agency typed. Either way it
 * bumps the password version, which signs every unlocked client out, so the
 * query is invalidated to pick up the new `passwordUpdatedAt`.
 */
export const useRegeneratePortalPasswordMutation = (propertyId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: regeneratePortalPasswordMutationKey,
		mutationFn: async (input?: { password?: string }) => {
			const response = await apiClient.properties[":id"].portal[
				"regenerate-password"
			].$post({
				param: { id: propertyId },
				json: { password: input?.password },
			});

			if (!response.ok) {
				return await throwPortalSiteError(
					response,
					"Failed to issue a new portal password",
				);
			}

			const body = await readPortalSiteJson<{ password: string }>(
				response,
			);

			return body.password;
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: propertyPortalQueryKey(propertyId),
			});
		},
	});
};

export const removePropertyPortalMutationKey = [
	"remove-property-portal",
] as const;

export const useRemovePropertyPortalMutation = (propertyId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: removePropertyPortalMutationKey,
		mutationFn: async () => {
			const response = await apiClient.properties[":id"].portal.$delete({
				param: { id: propertyId },
			});

			if (!response.ok) {
				return await throwPortalSiteError(
					response,
					"Failed to remove the client portal",
				);
			}

			// 204 — deliberately no body to read.
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: propertyPortalQueryKey(propertyId),
			});
		},
	});
};
